import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  FormHelperText,
  Drawer,
  Fade,
  withStyles,
} from '@material-ui/core';
import classNames from 'classnames';
import AbsencesField from '../absencesField';
import CustomButton from '../customButton';
import InputField from '../inputField';
import SelectField from '../selectField';
import ToggleButtonSwitch from '../toggleButtonSwitch';
import SelectWithRangeField from '../selectWithRange';
import CreatableTextsField from '../creatableTextsField';
import ColorPicker from '../colorPicker';
import Search from '../search';
import AddImageField from '../addImageField';
import Scrollbars from '../scrollbars';
import CustomCheckbox from '../customCheckbox';
import CheckboxGroup from '../checkboxGroup';
import CustomDatePicker from '../customDatePicker';
import CustomTimePicker from '../customTimePicker';
import SurveyPreview from '../surveyPreview';
import AttributeQuestionsForm from '../attributeQuestionsForm';
import QuestionnaireQuestionsField from '../questionnaireQuestionsField';
import AddUsersTableField from '../addUsersTableField';
import CustomRichTextEditor from '../customRichTextEditor';
import Impressions from '../impressions';
import PeoplePicker from '../peoplePicker';
import AddCoursesField from '../addCoursesField';
import ActionButton from '../actionButton';
import OneOnOneTimeIndicator from '../oneOnOneTimeIndicator';
import MeetingTopics from '../meetingTopics';
import CreatableTagsField from '../creatableTagsField';
import CustomSwitch from '../customSwitch';
import TasksField from '../tasksField';
import ProgressField from '../progressField';
import ReviewsField from '../reviewsField';
import PreviewField from '../previewField';
import A4DocumentPreview from '../a4DocumentPreview';
import SelectableListField from '../selectableListField';
import FiltersSetup from '../filtersSetup';
import MultipleDatePicker from '../multipleDatePicker';
import CycleStepsField from '../cycleStepsField';
import SettingsSummaryField from '../settingsSummaryField';
import CycleStepAssignmentsField from '../cycleStepAssignmentsField';
import ToggleableSettingsField from '../toggleableSettingsField';
import CustomRadioGroup from '../customRadioGroup';
import AddFileField from '../addFileField';
import DateRangeWithTextFields from '../dateRangeWithTextFields';
import TextFields from '../textFields';
import VoteField from '../voteField';
import RecruitmentStepsFields from '../recruitmentStepsFields';
import CollapsableFilters from '../collapsableFilters';
import ManageUsersList from '../manageUsersList';
import NumberStepper from '../numberStepper';
import SalaryRanges from '../salaryRanges';
import TextBoxWithTooltip from '../textBoxWithTooltip';
import AddAdditionalRolesField from '../addAdditionalRolesField';
import DocumentTypeFields from '../documentTypeFields';
import PeopleInfoFields from '../peopleInfoFields';
import UserAvatar from '../userAvatar';
import { formatDate } from '../../../utility/dateUtils';
import { getPersonFullName, showErrorMessage } from '../../../utility/uiUtils';
import { validateField, validateFileType } from '../../../utility/validation';
import {
  asyncDebounce,
  isArrayEmpty,
  isArray,
  getObjectToNumberArray,
  getFormattedUsers,
  removeObjectFromArray,
  replaceObjectInList,
  getItemIndex,
} from '../../../utility/helpers';
import { getSelectFieldOptions } from '../../../utility/formUtils';
import { getActivePageIndexFromQuill } from '../../../utility/a4DocumentPagination';
import { FIELD_TYPES } from '../../../constants/fieldTypes';
import {
  CREATE_SURVEY_FIELD_NAMES,
  EMPTY_QUESTIONNAIRE,
} from '../../../constants/survey';
import { ACTION_BUTTON_TYPES } from '../actionButton/config';
import { DEFAULT_FADE_IN, DEFAULT_FADE_OUT } from '../../../constants/fade';

const SHORTNAME = 'code';
const SAVE = 'save';
const SAVE_AND_ADD = 'saveAndAdd';
const SAVE_TYPE = 'save_type';
const DIRTY = 'isDirty';

const styles = ({ palette: { primary }, spacing, breakpoints }, props) => ({
  wholeScreen: {
    width: '100vw',
  },
  fullWidth: {
    width: 'calc(100vw - 200px)',
    [breakpoints.up('xl')]: {
      width: 'calc(100vw - 245px)',
    },
  },
  middleWidth: {
    width: 940,
  },
  largeWidth: {
    width: 600,
  },
  smallWidth: {
    width: 'auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    maxHeight: 71,
    padding: spacing(5, 6),
    borderBottom: `1px solid ${primary.bluish5}`,
  },
  headerFullWidth: {
    padding: spacing(5, 8),
  },
  title: {
    marginRight: spacing(2),
    wordBreak: 'break-word',
  },
  scrollWrapper: {
    height: 'calc(100% - 144px)',
    maxHeight: 'calc(100% - 144px)',
    width: '100%',
    minHeight: 0,
  },
  content: {
    boxSizing: 'border-box',
    padding: spacing(6),
    display: 'flex',
    width: '100%',
    minHeight: 0,
  },
  contentFullWidth: {
    padding: spacing(6, 8),
  },
  form: {
    width: '100%',
  },
  columns: {
    display: 'grid',
    gridTemplateColumns: `repeat(${props?.columns?.length || 1}, 1fr)`,
    columnGap: 1,
    height: 'calc(100vh - 147px)',
    minHeight: 0,
  },
  columnScrollWrapper: {
    boxSizing: 'border-box',
    boxShadow: `1px 0 0 0 ${primary.bluish5}`,
    position: 'relative',
    height: '100%',
    width: '100%',
    minHeight: 0,
  },
  columnContentWrapper: {
    boxSizing: 'border-box',
    display: 'flex',
    padding: spacing(6),
    width: '100%',
    minHeight: 0,
  },
  column: {
    boxSizing: 'border-box',
    width: '100%',
    minHeight: 0,
  },
  columnFieldFill: {
    height: '100%',
    minHeight: 0,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  columnFieldCenter: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    alignSelf: 'stretch',
    minHeight: 0,
    width: '100%',
    overflow: 'visible',
    '& > *': {
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
    },
  },
  columnTitle: {
    marginBottom: spacing(6),
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    maxHeight: 73,
    padding: spacing(4, 6),
    borderTop: `1px solid ${primary.bluish5}`,
    height: 40,
  },
  footerFullWidth: {
    padding: spacing(4, 8),
  },
  headerActions: {
    marginLeft: 'auto',
    display: 'flex',
  },
  footerWithPreview: {
    justifyContent: 'space-between',
  },
  field: {
    marginBottom: spacing(6),
  },
  hasError: {
    marginBottom: spacing(1),
  },
  divider: {
    '&::after': {
      content: '""',
      backgroundColor: primary.bluish5,
      display: 'block',
      marginBottom: spacing(6),
      marginTop: spacing(6),
      height: 1,
      width: '100%',
    },
    '&$hasError': {
      marginBottom: 0,
      '&::after': {
        content: '""',
        backgroundColor: primary.bluish5,
        display: 'block',
        marginBottom: spacing(6),
        marginTop: spacing(1),
        height: 1,
        width: '100%',
      },
    },
  },
  saveButton: {
    backgroundColor: primary.blue1,
    border: `2px solid ${primary.blue1}`,
    fontFamily: 'ProximaNova-Bold',
    fontSize: 16,
    lineHeight: '24px',
    padding: spacing(2, 4),
    marginLeft: spacing(4),
  },
  saveButtonSecondary: {
    backgroundColor: primary.white,
    color: primary.blue1,
    marginLeft: 8,
    '&:hover': {
      backgroundColor: primary.white,
    },
  },
  cancelButton: {
    backgroundColor: primary.white,
    border: `1px solid ${primary.bluish1}`,
    borderRadius: 44,
    color: primary.bluish1,
    fontFamily: 'ProximaNova-Bold',
    fontSize: 16,
    lineHeight: '24px',
    padding: '8px 16px',
    maxHeight: 40,
  },
  saveAndAddButton: {
    backgroundColor: primary.blue1,
    border: `1px solid ${primary.blue1}`,
    borderRadius: 44,
    color: primary.white,
    fontFamily: 'ProximaNova-Bold',
    fontSize: 16,
    lineHeight: '24px',
    padding: '8px 16px',
    marginLeft: 8,
    maxHeight: 40,
    '&:hover': {
      backgroundColor: primary.blue1,
    },
  },
  saveWithDraftButton: {
    marginLeft: spacing(2),
  },
  saveDraftButton: {
    marginLeft: 0,
  },
  highlightedLabel: {
    fontFamily: 'ProximaNova-Bold',
    fontSize: 18,
    lineHeight: '20px',
  },
  checkbox: {
    alignItems: 'flex-start',
  },
  deleteIcon: {
    marginRight: spacing(1),
  },
  columnScrollY: {
    right: 3,
    '& .os-scrollbar-vertical': {
      paddingTop: spacing(6),
      paddingBottom: spacing(6),
      [breakpoints.up('xLg')]: {
        paddingTop: spacing(8),
      },
    },
    [breakpoints.up('xLg')]: {
      right: 6,
    },
  },
  scrollY: {
    right: 4,
    '& .os-scrollbar-vertical': {
      paddingTop: spacing(6),
      paddingBottom: spacing(6),
    },
  },
  scrollX: {
    backgroundColor: primary.bluish9,
    left: 24,
    bottom: 6,
    width: 'calc(100% - 48px)',
    height: 8,
  },
  scrollXFullWidth: {
    left: 31,
  },
  scrollYFullWidth: {
    top: 31,
  },
  scroll: {
    backgroundColor: primary.bluish7,
  },
  scroller: {
    marginRight: spacing(5),
    marginBottom: spacing(5),
  },
  createdBy: {
    color: primary.bluish4,
  },
  readOnly: {
    wordBreak: 'break-word',
  },
  preview: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  textEditor: {
    '& .ql-toolbar': {
      borderTopLeftRadius: 3,
      borderTopRightRadius: 3,
    },
    '& .ql-container': {
      height: 188,
      borderBottomLeftRadius: 3,
      borderBottomRightRadius: 3,
      '& .ql-blank::before': {
        lineHeight: '24px',
      },
    },
  },
  titleIcon: {
    marginRight: spacing(2),
    width: 32,
    height: 33,
  },
  privateNoteError: {
    bottom: -24,
  },
  meetingStatus: {
    marginBottom: spacing(3),
  },
  notifyAll: {
    display: 'flex',
    alignItems: 'center',
    marginRight: 'auto',
  },
  checkboxLabel: {
    width: '100%',
    maxWidth: 'calc(100% - 24px)',
    flexShrink: 0,
  },
});

const QUESTIONS_DEFAULT_NAME = 'questions';

class CustomFormDrawerLayout extends PureComponent {
  constructor(props) {
    super(props);
    this.handleValidateSingleField = asyncDebounce(
      this.handleValidateSingleField
    );
    this.questionnaireFlushRef = null;
    this.state = {
      isExpandedOnFirstOpen: true,
      isExpandedOnFirstOpenFramework: true,
      contentNode: null,
      a4PreviewActivePage: 0,
    };
  }

  handleA4PreviewPageSync = (range, quill) => {
    if (!quill?.root) {
      return;
    }

    const pageIndex = getActivePageIndexFromQuill(quill.root.innerHTML, quill);

    if (pageIndex === this.state.a4PreviewActivePage) {
      return;
    }

    this.setState({ a4PreviewActivePage: pageIndex });
  };

  setContentNode = node => {
    const { contentNode } = this.state;

    if (contentNode !== node) {
      this.setState({ contentNode: node });
    }
  };

  resetDependants = ({
    fieldDependants,
    reverse,
    fieldName,
    currentFieldValue,
  }) => {
    const {
      values,
      initialValues,
      errors,
      setFieldValue,
      setFieldError,
      setValues,
      setErrors,
    } = this.props;

    if (fieldDependants && fieldDependants.length) {
      fieldDependants.map(async dependant => {
        if (dependant.manualResetFn) {
          return dependant.manualResetFn({
            fieldName,
            values,
            initialValues,
            currentFieldValue,
            dependantValue: values[dependant.name],
            errors,
            setValues,
            setFieldValue,
            setErrors,
            setFieldError,
          });
        }
        if (dependant.callbackFnc) {
          dependant.callbackFnc(
            values[fieldName],
            currentFieldValue,
            values[dependant.name]
          );
        } else if (dependant.resetFnc) {
          const dependantValue = await dependant.resetFnc(
            values[fieldName],
            currentFieldValue,
            values[dependant.name],
            initialValues,
            values
          );
          setFieldValue(dependant.name, dependantValue);
        } else {
          setFieldValue(
            dependant.name,
            reverse && dependant.reverse ? !dependant.value : dependant.value
          );
        }

        return setFieldError(dependant.name, null);
      });
    }
  };

  getFieldOptions = field => {
    const { values } = this.props;
    if (
      field.parent &&
      isArray(values[field.parent]) &&
      !isArrayEmpty(values[field.parent])
    ) {
      return this.props[field.optionsKey].filter(
        value =>
          !values[field.parent].some(
            parentValue =>
              parentValue === value || parentValue?.id === value?.id
          )
      );
    }

    return this.props[field.optionsKey] || [];
  };

  getFieldValue = field => {
    const { values } = this.props;

    if (field?.fieldGroup) {
      return values[field.fieldGroup]?.[field.name];
    }

    return values?.[field.name];
  };

  getIsFieldDisabled = (field, values) => {
    if (field.parent && !values[field.parent.name]) {
      return true;
    }

    if (field.isDisabledCheck?.(values)) {
      return true;
    }

    return Boolean(field.isDisabled);
  };

  handleClose = () => {
    const { onClose, resetForm } = this.props;

    this.setState({
      a4PreviewActivePage: 0,
    });
    onClose();
    resetForm();
  };

  handleValidateSingleField = (
    field,
    value,
    initialValue,
    values,
    canSkipValidation
  ) => {
    const { errors, setFieldError } = this.props;

    return validateField(
      field,
      value,
      initialValue,
      values,
      canSkipValidation
    ).then(errorType => {
      if (field.fieldGroup) {
        setFieldError(field.fieldGroup, {
          ...errors?.[field.fieldGroup],
          [field.name]: errorType,
        });
      } else {
        setFieldError(field.name, errorType);
      }
      return errorType;
    });
  };

  onValidate = field => value => {
    this.handleValidateSingleField(field, value);
  };

  onResetDependants = field => value => {
    this.resetDependants({
      fieldDependants: field.dependants,
      fieldName: field.name,
      currentFieldValue: value,
    });
  };

  onChange = field => updatedValue => {
    const { setFieldValue } = this.props;

    setFieldValue(field.name, updatedValue);
  };

  onChangeMultipleField = field => value => {
    const { initialValues, values, setFieldValue } = this.props;

    this.handleValidateSingleField(
      field,
      value,
      initialValues[field.name],
      values
    );
    this.resetDependants({
      fieldDependants: field.dependants,
      fieldName: field.name,
      currentFieldValue: value,
    });
    setFieldValue(field.name, value);
  };

  onToggleSettings = field => updatedValue => {
    const { setFieldValue } = this.props;

    this.resetDependants({
      fieldDependants: field.dependants,
      fieldName: field.name,
      currentFieldValue: updatedValue,
    });

    setFieldValue(field.settingsFor, updatedValue);
  };

  onToggleUserExclusion = field => updatedValue => {
    const { initialValues, values, setFieldValue } = this.props;

    this.handleValidateSingleField(
      field,
      updatedValue,
      initialValues[field.fieldGroup]?.[field?.name]
    );

    const updatedGroupValue = {
      ...values[field.fieldGroup],
      [field.name]: updatedValue,
    };
    return setFieldValue(field.fieldGroup, updatedGroupValue);
  };

  handleInputChange = async (e, field) => {
    const { setFieldValue, initialValues, values } = this.props;

    const updatedValue =
      field.name === SHORTNAME ? e.target.value.toUpperCase() : e.target.value;

    this.handleValidateSingleField(
      field,
      updatedValue,
      field?.fieldGroup
        ? initialValues[field.fieldGroup]?.[field?.name]
        : initialValues[field.name],
      values
    );

    if (field?.fieldGroup) {
      const updatedGroupValue = {
        ...values[field.fieldGroup],
        [field.name]: updatedValue,
      };
      return setFieldValue(field.fieldGroup, updatedGroupValue);
    }

    setFieldValue(field.name, updatedValue);
  };

  handleAddUsersChange = field => (value, rowIndex) => {
    const { setFieldValue, initialValues } = this.props;
    this.handleValidateSingleField(
      {
        ...field,
        validators: [
          {
            validator: users =>
              field.usersValidator(
                users,
                field.columns,
                initialValues[field.name],
                rowIndex
              ),
            isMultiple: true,
          },
        ],
      },
      value
    );
    setFieldValue(field.name, value);
  };

  handleChangeSelectWithRangeValue = field => value => {
    const { setFieldValue } = this.props;

    this.handleValidateSingleField(field, value);
    setFieldValue(field.name, value);
  };

  handleChangeRange = field => (value, item, fieldName) => {
    const { setFieldValue, values } = this.props;

    if (item[fieldName] !== value) {
      const attributeIndex = values[field.name].findIndex(
        attr => attr.id === item.id
      );
      const newAttributes = replaceObjectInList(
        values[field.name],
        attributeIndex,
        {
          ...item,
          [fieldName]: value,
        }
      );
      this.handleValidateSingleField(field, newAttributes);
      setFieldValue(field.name, newAttributes);
    }
  };

  handleAttributeCommentsSwitchChange = field => (currentAttribute, value) => {
    const { setFieldValue, values } = this.props;

    const attributeIndex = getItemIndex(
      field?.fieldGroup
        ? values[field.fieldGroup]?.[field.name]
        : values[field.name],
      currentAttribute.id
    );
    const updatedAttributes = replaceObjectInList(
      field?.fieldGroup
        ? values[field.fieldGroup]?.[field.name]
        : values[field.name],
      attributeIndex,
      {
        ...currentAttribute,
        [field.addCommentsFieldName]: value,
      }
    );

    if (field?.fieldGroup) {
      const updatedGroupValue = {
        ...values[field.fieldGroup],
        [field.name]: updatedAttributes,
      };
      return setFieldValue(field.fieldGroup, updatedGroupValue);
    }

    setFieldValue(field.name, updatedAttributes);
  };

  handleChangeSelectValue = field => value => {
    const { setFieldValue, initialValues, values } = this.props;
    if (values[field.name] !== value) {
      if (field.callbackFnc) {
        return field.callbackFnc(value);
      }

      this.handleValidateSingleField(
        field,
        value,
        initialValues[field.name],
        values
      );
      this.resetDependants({
        fieldDependants: field.dependants,
        fieldName: field.name,
        currentFieldValue: value,
      });
      if (field.isExternalyManagedValue) return;

      if (field?.fieldGroup) {
        const updatedGroupValue = {
          ...values[field.fieldGroup],
          [field.name]: value,
        };
        return setFieldValue(field.fieldGroup, updatedGroupValue);
      }

      setFieldValue(field.name, value);
    }
  };

  handleDateTimeChange = field => value => {
    const { initialValues, values, setFieldValue } = this.props;
    const formatedValue = field.valueFormat
      ? formatDate(value, field.valueFormat)
      : value;

    this.handleValidateSingleField(
      field,
      formatedValue,
      initialValues[field.name],
      values
    );
    this.resetDependants({
      fieldDependants: field.dependants,
      fieldName: field.name,
      currentFieldValue: formatedValue,
    });

    if (field.isExternalyManagedValue) return;

    if (field?.fieldGroup) {
      const updatedGroupValue = {
        ...values[field.fieldGroup],
        [field.name]: formatedValue,
      };
      return setFieldValue(field.fieldGroup, updatedGroupValue);
    }

    setFieldValue(field.name, formatedValue);
  };

  handleMeetingTopicsChange = field => value => {
    const { setFieldValue } = this.props;

    this.handleValidateSingleField(field, value);
    setFieldValue(field.name, value);
  };

  handleRecommendedPlacementChange = field => placements => {
    const { setFieldValue } = this.props;

    this.handleValidateSingleField(field, placements);
    setFieldValue(field.name, placements);
  };

  handleSearch = field => searchTerm => {
    const { values, setFieldValue } = this.props;

    this.resetDependants({ fieldDependants: field.dependants });
    setFieldValue(field.name, searchTerm);
    field.onSearch(searchTerm, values);
  };

  handleToggleChange = field => value => {
    const { setFieldValue, resetForm } = this.props;
    setFieldValue(field.name, value);
    this.resetDependants({
      fieldDependants: field.dependants,
      fieldName: field.name,
      currentFieldValue: value,
    });

    if (field.onValueChange) {
      field.onValueChange(value, { resetForm });
    }
  };

  handlePhotoUpload = field => photo => {
    const { setFieldValue } = this.props;
    this.handleValidateSingleField(field, photo);
    setFieldValue(field.name, photo);
  };

  handleFileReject = field => () => {
    const { setFieldError, translations } = this.props;
    const errorType =
      field.validators?.find(validator => validator.type === 'fileType')
        ?.type || 'fileType';

    if (field.required) {
      setFieldError(field.name, errorType);
      return;
    }

    showErrorMessage(translations.validationMessages[errorType]);
  };

  handleFileUpload = field => file => {
    const { setFieldValue, values, setFieldError, translations } = this.props;

    if (
      file &&
      field.types &&
      !validateFileType(file, Object.keys(field.types))
    ) {
      const errorType =
        field.validators?.find(validator => validator.type === 'fileType')
          ?.type || 'fileType';

      if (field.required) {
        setFieldError(field.name, errorType);
        return;
      }

      showErrorMessage(translations.validationMessages[errorType]);
      setFieldValue(field.name, null);
      setFieldError(field.name, null);
      return;
    }

    this.resetDependants({
      fieldDependants: field.dependants,
      fieldName: field.name,
      currentFieldValue: file,
    });
    this.handleValidateSingleField(field, file);
    setFieldValue(
      field.name,
      field.isMultiple ? [...values[field.name], file] : file
    );
  };

  handleFilterResults = field => async selected => {
    const { initialValues, setFieldValue } = this.props;

    const filteredValues = await field.getResults(selected);
    const currentFieldValue = {
      selected,
      filteredValues,
      isDisabled: false,
    };

    this.handleValidateSingleField(
      field,
      currentFieldValue,
      initialValues[field.name]
    );
    this.resetDependants({
      fieldDependants: field.dependants,
      fieldName: field.name,
      currentFieldValue,
    });
    if (field.isExternalyManagedValue) return;

    setFieldValue(field.name, currentFieldValue);
  };

  handleAutocompleteSelect = async (field, selectedValues, dependants) => {
    const { initialValues, values, setFieldValue } = this.props;

    this.handleValidateSingleField(
      field,
      selectedValues,
      initialValues[field.name],
      values
    );
    this.resetDependants({
      fieldDependants: dependants,
      fieldName: field.name,
      currentFieldValue: selectedValues,
    });
    if (field.isExternalyManagedValue) return;

    setFieldValue(field.name, selectedValues);
  };

  handleCheckboxChange = field => value => {
    const { setFieldValue, setFieldError, values, initialValues } = this.props;
    const { HAS_QUESTIONNAIRE, QUESTIONNAIRE } = CREATE_SURVEY_FIELD_NAMES;
    const nextValue = field.isReverse ? !value : value;

    this.resetDependants({
      fieldName: field.name,
      fieldDependants: field.dependants,
      currentFieldValue: nextValue,
    });
    this.handleValidateSingleField(
      field,
      nextValue,
      initialValues[field.name],
      values
    );
    if (field.isExternalyManagedValue) return;
    if (field?.fieldGroup) {
      const updatedGroupValue = {
        ...values[field.fieldGroup],
        [field.name]: nextValue,
      };
      setFieldValue(field.fieldGroup, updatedGroupValue, false);

      if (field.name === HAS_QUESTIONNAIRE) {
        setFieldError(QUESTIONNAIRE, null);
      }

      return undefined;
    }
    setFieldValue(field.name, nextValue, false);

    if (field.name === HAS_QUESTIONNAIRE) {
      setFieldError(QUESTIONNAIRE, null);
    }
  };

  handleSliderChange = field => value => {
    const { values, setFieldValue } = this.props;

    if (value !== values[field.name]) {
      setFieldValue(field.name, value);
    }
  };

  handleCheckboxGroupChange = field => itemId => {
    const { values, setFieldValue, translations } = this.props;
    const checkedItems = values[field.name] || [];
    const items = field.getItems
      ? field.getItems(values, translations?.formLabels)
      : [];
    const clickedItem = items.find(item => item.id === itemId);
    const linkedIds =
      field.syncByKey && clickedItem?.[field.syncByKey] != null
        ? items
            .filter(
              item =>
                !item.isDisabled &&
                item[field.syncByKey] === clickedItem[field.syncByKey]
            )
            .map(item => item.id)
        : [itemId];
    const isChecked = checkedItems.some(checkedItem => checkedItem === itemId);

    if (isChecked) {
      return setFieldValue(
        field.name,
        checkedItems.filter(checkedItem => !linkedIds.includes(checkedItem))
      );
    }

    return setFieldValue(field.name, [
      ...new Set([...checkedItems, ...linkedIds]),
    ]);
  };

  handleRadioGroupChange = field => value => {
    const { initialValues, setFieldValue } = this.props;
    this.handleValidateSingleField(field, value, initialValues[field.name]);
    setFieldValue(field.name, value);
  };

  handleMultipleValuesFieldChange = field => value => {
    const { initialValues, setFieldValue } = this.props;

    this.handleValidateSingleField(field, value, initialValues[field.name]);
    this.resetDependants({
      fieldDependants: field.dependants,
      fieldName: field.name,
      currentFieldValue: value,
    });
    setFieldValue(field.name, value);
  };

  handleFildsChange = field => value => {
    const { initialValues, setFieldValue } = this.props;

    this.handleValidateSingleField(field, value, initialValues[field.name]);
    this.resetDependants({
      fieldDependants: field.dependants,
      fieldName: field.name,
      currentFieldValue: value,
    });
    setFieldValue(field.name, value);
  };

  handleVoteCandidateChange = field => value => {
    const { initialValues, setFieldValue } = this.props;

    this.handleValidateSingleField(field, value, initialValues[field.name]);

    setFieldValue(field.name, value);
  };

  handleAddCourse = field => value => {
    const { values, setFieldValue } = this.props;
    const selectedCourses = values[field.name];
    const isSelected = selectedCourses.some(course => course.id === value.id);

    if (isSelected) {
      return setFieldValue(
        field.name,
        removeObjectFromArray(selectedCourses, value)
      );
    }

    return setFieldValue(field.name, [...selectedCourses, value]);
  };

  handleReviewChange = field => value => {
    const { setFieldValue } = this.props;

    this.resetDependants({
      fieldName: field.name,
      fieldDependants: field.dependants,
      currentFieldValue: value,
    });

    this.handleValidateSingleField(field, value, null, null, true);

    setFieldValue(field.name, value);
  };

  handleSelectableListChange = field => value => {
    const { setFieldValue } = this.props;

    this.handleValidateSingleField(field, value);
    this.setState({
      isExpandedOnFirstOpen: true,
      isExpandedOnFirstOpenFramework: true,
    });

    setFieldValue(field.name, value);
  };

  handleAttributeQuestionsChange = field => (fieldNames, fieldValues) => {
    const { setFieldValue, initialValues, values } = this.props;
    fieldNames.forEach(fieldName => {
      setFieldValue(fieldName, fieldValues[fieldName]);
      if (fieldName === QUESTIONS_DEFAULT_NAME) {
        this.handleValidateSingleField(
          field.attributeQuestions,
          fieldValues[fieldName],
          initialValues,
          values
        );
      }
    });
  };

  handleQuestionnaireQuestionsChange = field => updatedQuestions => {
    const { setFieldValue } = this.props;

    setFieldValue(field.name, updatedQuestions, false);
  };

  handleQuestionnaireValidate = field => pendingQuestionnaire => {
    const { values, initialValues, setFieldError } = this.props;

    return validateField(
      field,
      pendingQuestionnaire,
      initialValues[field.name],
      { ...values, [field.name]: pendingQuestionnaire }
    ).then(errorType => {
      setFieldError(field.name, errorType || null);
    });
  };

  handleRegisterQuestionnaireFlush = flush => {
    this.questionnaireFlushRef = flush;
  };

  renderTitle = titleText => {
    const { translations, values, getTitle } = this.props;
    const customTitle = getTitle(translations, values, !!values.id);

    if (customTitle) {
      return customTitle;
    }

    if (titleText) {
      return titleText;
    }
    if (values.id) {
      return translations.edit;
    }
    return translations.add;
  };

  renderField = field => {
    const {
      classes,
      values,
      translations,
      errors,
      setFieldValue,
      setFieldError,
      setDialogVisibility,
      dialogs,
      initialValues,
    } = this.props;
    const { contentNode } = this.state;

    const {
      ABSENCES,
      ADD_COURSES,
      ADD_ADDITIONAL_ROLES_FIELD,
      ADD_USERS_TABLE,
      ADD_IMAGE,
      CYCLE_STEPS,
      CYCLE_STEP_ASSIGNMENTS,
      TEXT,
      SELECT,
      SELECT_WITH_RANGE,
      BUTTON_SWITCH,
      COLOR_PICKER,
      CREATED_BY,
      CREATABLE_TAGS,
      CREATABLE_TEXTS,
      ATTRIBUTES_QUESTIONS_FORM,
      SEARCH,
      SELECTABLE_LIST,
      CHECKBOX_GROUP,
      RADIO_GROUP,
      DATE_PICKER,
      RICH_TEXT_EDITOR,
      SUBTITLE,
      SUBTITLE_WITH_TOOLTIP,
      TIME_PICKER,
      SURVEY_PREVIEW,
      PREVIEW,
      A4_DOCUMENT_PREVIEW,
      CHECKBOX,
      READ_ONLY,
      PEOPLE_PICKER,
      PEOPLE_INFO,
      USER_AVATAR,
      MANAGE_USERS_LIST,
      MEETING_IMPRESSION,
      MEETING_STATUS,
      MEETING_AGENDA,
      MULTIPLE_DATE_PICKER,
      FILTERS_SETUP,
      COLLAPSABLE_FILTERS,
      SETTINGS_SUMMARY,
      SWITCH,
      SLIDER,
      TASKS,
      TOGGLEABLE_SETTINGS,
      REVIEWS,
      UPLOAD_FILE,
      DATE_RANGE_WITH_TEXT_LIST,
      SALARY_RANGE,
      TEXT_LIST,
      DOCUMENT_TYPE_FIELDS,
      VOTE,
      RECRUITMENT_STEPS,
      NUMBER_STEPPER,
      QUESTIONNAIRE_QUESTIONS,
    } = FIELD_TYPES;
    const { formLabels, validationMessages } = translations;
    const { labelIcon: LabelIcon } = field;
    const hasError = field?.fieldGroup
      ? !!errors?.[field?.fieldGroup]?.[field.name]
      : !!errors?.[field.name];
    const errorMessage = field?.fieldGroup
      ? validationMessages?.[errors?.[field?.fieldGroup]?.[field.name]]
      : validationMessages?.[errors?.[field.name]];

    const fieldClasses = classNames(
      {
        [classes.field]: !field.hasDivider && !field.shouldRemoveFieldSpacing,
        [classes.hasError]:
          hasError && !field.isMultiple && !field.shouldRemoveFieldError,
        [classes.divider]: field.hasDivider,
      },
      field.fieldWrapperClass
    );
    const fieldValue = this.getFieldValue(field);

    switch (field.type) {
      case TEXT:
        if (field.isToggleable) {
          return (
            <Fade
              key={`input_field_${field?.fieldGroup || ''}${field.name}`}
              in={field.shouldRender(values)}
              timeout={{
                enter: DEFAULT_FADE_IN,
                exit: field.shouldFadeOutInstant ? 0 : DEFAULT_FADE_OUT,
              }}
              unmountOnExit
            >
              <div key={field.name} className={fieldClasses}>
                <InputField
                  id={field.name}
                  name={field.name}
                  type={field.inputType || field.type}
                  customRootClass={field.customRootClass}
                  label={
                    formLabels?.[field.translationKey || field.name]?.label
                  }
                  labelExplanation={
                    formLabels?.[field.translationKey || field.name]
                      ?.labelExplanation
                  }
                  labelHelp={
                    formLabels?.[field.translationKey || field.name]?.labelHelp
                  }
                  placeholder={
                    formLabels?.[field.translationKey || field.name]
                      ?.placeholder || field.placeholder
                  }
                  required={field.required}
                  value={
                    field.inputType === 'number' ? fieldValue : fieldValue || ''
                  }
                  multiline={field.multiline}
                  rows={field.rows}
                  startAdornment={
                    field.startAdornment
                      ? field.startAdornment(values)
                      : undefined
                  }
                  error={hasError}
                  errorMessage={errorMessage}
                  onChange={e => this.handleInputChange(e, field)}
                  disabled={
                    field.isDisabled ||
                    (field.isDisabledCheck
                      ? field.isDisabledCheck(values)
                      : undefined)
                  }
                  adornmentIcon={field.adornmentIcon}
                  isIconOnStart={field.isIconOnStart}
                  fullWidth
                />
              </div>
            </Fade>
          );
        }

        return (
          <div key={field.name} className={fieldClasses}>
            <InputField
              id={field.name}
              name={field.name}
              type={field.inputType || field.type}
              customRootClass={field.customRootClass}
              label={formLabels?.[field.translationKey || field.name]?.label}
              labelExplanation={
                formLabels?.[field.translationKey || field.name]
                  ?.labelExplanation
              }
              labelHelp={
                formLabels?.[field.translationKey || field.name]?.labelHelp
              }
              placeholder={
                formLabels?.[field.translationKey || field.name]?.placeholder ||
                field.placeholder
              }
              required={field.required}
              value={
                field.inputType === 'number' ? fieldValue : fieldValue || ''
              }
              multiline={field.multiline}
              rows={field.rows}
              startAdornment={
                field.startAdornment ? field.startAdornment(values) : undefined
              }
              error={hasError}
              errorMessage={errorMessage}
              onChange={e => this.handleInputChange(e, field)}
              disabled={
                field.isDisabled ||
                (field.isDisabledCheck
                  ? field.isDisabledCheck(values)
                  : undefined)
              }
              adornmentIcon={field.adornmentIcon}
              isIconOnStart={field.isIconOnStart}
              fullWidth
            />
          </div>
        );
      case SELECT:
        if (field.isToggleable) {
          return (
            <Fade
              key={`select_field_${field?.fieldGroup || ''}_${field.name}`}
              in={field.shouldRender(values)}
              timeout={{
                enter: DEFAULT_FADE_IN,
                exit: field.shouldFadeOutInstant ? 0 : DEFAULT_FADE_OUT,
              }}
              unmountOnExit
            >
              <div key={field.name} className={fieldClasses}>
                <SelectField
                  parentNode={contentNode}
                  id={field.name}
                  className={field.className}
                  labelClass={field.labelClass}
                  label={formLabels[field.translationKey || field.name]?.label}
                  menuClass={field.menuClass}
                  menuListClass={field.menuListClass}
                  shouldRemoveLabel={field.shouldRemoveLabel}
                  labelHelp={
                    formLabels[field.translationKey || field.name]?.labelHelp
                  }
                  placeholder={
                    formLabels[field.translationKey || field.name]?.placeholder
                  }
                  creatableOptionLabel={
                    formLabels[field.translationKey || field.name]
                      ?.creatableOptionLabel
                  }
                  startIcon={field.startIcon}
                  required={field.required}
                  options={getSelectFieldOptions(
                    field,
                    values,
                    this.props[field.optionsKey],
                    this.props[field?.parent?.options]
                  )}
                  parser={field.parser}
                  value={fieldValue}
                  renderRolesTooltip={
                    formLabels[field.name]?.renderRolesTooltip
                  }
                  creatableOptionField={field.creatableOptionField}
                  shouldReturnOption={field.shouldReturnOption}
                  shouldDisableSort={field.shouldDisableSort}
                  shouldCalculateSelectionHeight={
                    field.shouldCalculateSelectionHeight
                  }
                  hasAttributePreview={field.hasAttributePreview}
                  isCategorized={field.isCategorized}
                  isDisabled={this.getIsFieldDisabled(field, values)}
                  isTag={field.isTag}
                  isUser={field.isUser}
                  isAttribute={field.isAttribute}
                  isAttributeDraggable={
                    field.isAttributeDraggable &&
                    !this.getIsFieldDisabled(field, values)
                  }
                  hasHash={field.hasHash}
                  isIcon={field.isIcon}
                  hasCategoryColorBox={field.hasCategoryColorBox}
                  isSearchDisabled={
                    field.isSearchDisabled ||
                    this.getIsFieldDisabled(field, values)
                  }
                  isMulti={field.isMulti}
                  isCreatable={field.isCreatable}
                  hasError={hasError}
                  errorMessage={errorMessage}
                  isClearable={field.isClearable}
                  hasColorBox={field.hasColorBox}
                  hasAttributeComments={field.hasAttributeComments}
                  onAttributePreview={field.onAttributePreview}
                  onToggleAttributeComments={this.handleAttributeCommentsSwitchChange(
                    field
                  )}
                  onCreateOption={field.onCreateOption}
                  onChange={this.handleChangeSelectValue(field)}
                  customStyles={field.customStyles}
                  isFullWidth
                />
              </div>
            </Fade>
          );
        }
        return (
          <div key={field.name} className={fieldClasses}>
            <SelectField
              parentNode={contentNode}
              id={field.name}
              className={field.className}
              labelClass={field.labelClass}
              label={formLabels[field.translationKey || field.name]?.label}
              menuClass={field.menuClass}
              menuListClass={field.menuListClass}
              shouldRemoveLabel={field.shouldRemoveLabel}
              labelHelp={
                formLabels[field.translationKey || field.name]?.labelHelp
              }
              placeholder={
                formLabels[field.translationKey || field.name]?.placeholder
              }
              creatableOptionLabel={
                formLabels[field.translationKey || field.name]
                  ?.creatableOptionLabel
              }
              startIcon={field.startIcon}
              required={field.required}
              options={getSelectFieldOptions(
                field,
                values,
                this.props[field.optionsKey],
                this.props[field?.parent?.options]
              )}
              parser={field.parser}
              value={fieldValue}
              renderRolesTooltip={formLabels[field.name]?.renderRolesTooltip}
              creatableOptionField={field.creatableOptionField}
              shouldReturnOption={field.shouldReturnOption}
              shouldDisableSort={field.shouldDisableSort}
              shouldCalculateSelectionHeight={
                field.shouldCalculateSelectionHeight
              }
              isCategorized={field.isCategorized}
              isDisabled={this.getIsFieldDisabled(field, values)}
              isIcon={field.isIcon}
              isTag={field.isTag}
              isUser={field.isUser}
              isAttribute={field.isAttribute}
              isAttributeDraggable={
                field.isAttributeDraggable &&
                !this.getIsFieldDisabled(field, values)
              }
              hasAttributePreview={field.hasAttributePreview}
              hasHash={field.hasHash}
              hasCategoryColorBox={field.hasCategoryColorBox}
              isSearchDisabled={
                field.isSearchDisabled || this.getIsFieldDisabled(field, values)
              }
              isMulti={field.isMulti}
              isCreatable={field.isCreatable}
              hasError={hasError}
              errorMessage={errorMessage}
              isClearable={field.isClearable}
              hasColorBox={field.hasColorBox}
              hasAttributeComments={field.hasAttributeComments}
              onToggleAttributeComments={this.handleAttributeCommentsSwitchChange(
                field
              )}
              onAttributePreview={field.onAttributePreview}
              onCreateOption={field.onCreateOption}
              onChange={this.handleChangeSelectValue(field)}
              customStyles={field.customStyles}
              isFullWidth
            />
          </div>
        );
      case SELECT_WITH_RANGE:
        return (
          <div key={field.name} className={fieldClasses}>
            <SelectWithRangeField
              id={field.name}
              labelClass={field.labelClass}
              label={formLabels[field.translationKey || field.name]?.label}
              shouldRemoveLabel={field.shouldRemoveLabel}
              labelHelp={
                formLabels[field.translationKey || field.name]?.labelHelp
              }
              placeholder={
                formLabels[field.translationKey || field.name]?.placeholder
              }
              creatableOptionLabel={
                formLabels[field.translationKey || field.name]
                  ?.creatableOptionLabel
              }
              startIcon={field.startIcon}
              required={field.required}
              options={getSelectFieldOptions(
                field,
                values,
                this.props[field.optionsKey],
                this.props[field?.parent?.options]
              )}
              parser={field.parser}
              value={fieldValue}
              renderRolesTooltip={formLabels[field.name]?.renderRolesTooltip}
              creatableOptionField={field.creatableOptionField}
              shouldReturnOption={field.shouldReturnOption}
              shouldDisableSort={field.shouldDisableSort}
              isCategorized={field.isCategorized}
              isDisabled={
                (field.parent && !values[field.parent.name]) ||
                (field.isDisabledCheck
                  ? field.isDisabledCheck(values)
                  : undefined) ||
                field.isDisabled
              }
              isAttributeDraggable={field.isAttributeDraggable}
              hasHash={field.hasHash}
              hasCategoryColorBox={field.hasCategoryColorBox}
              isSearchDisabled={field.isSearchDisabled}
              isCreatable={field.isCreatable}
              hasError={hasError}
              errorMessage={errorMessage}
              isClearable={field.isClearable}
              hasColorBox={field.hasColorBox}
              onCreateOption={field.onCreateOption}
              onChange={this.handleChangeSelectWithRangeValue(field)}
              onChangeRange={this.handleChangeRange(field)}
              customStyles={field.customStyles}
              isFullWidth
            />
          </div>
        );
      case SELECTABLE_LIST:
        return (
          <div key={field.name} className={fieldClasses}>
            <SelectableListField
              options={field.options}
              label={field.label}
              selected={fieldValue}
              hasError={hasError}
              errorMessage={errorMessage}
              onSelect={this.handleSelectableListChange(field)}
            />
          </div>
        );
      case BUTTON_SWITCH:
        return (
          <div
            key={field.name}
            className={classNames(classes.field, field.fieldWrapperClass)}
          >
            <ToggleButtonSwitch
              id={field.name}
              label={formLabels[field.translationKey || field.name].label}
              value={fieldValue}
              items={field.items}
              onSelect={this.handleToggleChange(field)}
            />
          </div>
        );
      case COLOR_PICKER:
        return (
          <div
            key={field.name}
            className={classNames(classes.field, field.fieldWrapperClass)}
          >
            <ColorPicker
              className={field.className}
              label={formLabels[field.translationKey || field.name].label}
              tooltipText={
                formLabels[field.translationKey || field.name].tooltip
              }
              selectedColor={fieldValue}
              collorPalette={field.collorPalette}
              shouldDisableCustomColorPick={field.shouldDisableCustomColorPick}
              onColorPick={color => setFieldValue(field.name, color)}
            />
          </div>
        );
      case CREATED_BY:
        return (
          <div key={field.name} className={field.fieldWrapperClass}>
            <FormHelperText>
              {formLabels[field.translationKey || field.name].label}
            </FormHelperText>
            <Typography variant="subtitle1" className={classes.createdBy}>
              {getPersonFullName(values[field.name])}
            </Typography>
          </div>
        );
      case USER_AVATAR:
        return (
          <div
            key={field.name}
            className={classNames(classes.field, field.fieldWrapperClass)}
          >
            <FormHelperText>
              {formLabels[field.translationKey || field.name].label}
            </FormHelperText>
            <UserAvatar user={values[field.name]} {...field.avatarProps} />
          </div>
        );
      case SEARCH:
        return (
          <div key={field.name} className={fieldClasses}>
            <Search
              label={formLabels[field.translationKey || field.name].label}
              placeholder={
                formLabels[field.translationKey || field.name].placeholder
              }
              onChange={this.handleSearch(field)}
              isFormField
            />
          </div>
        );
      case ADD_IMAGE:
        return (
          <div key={field.name} className={fieldClasses}>
            <AddImageField
              label={formLabels[field.translationKey || field.name].label}
              placeholder={
                formLabels[field.translationKey || field.name].placeholder
              }
              image={fieldValue || null}
              hasError={hasError}
              errorMessage={errorMessage}
              onAddImage={this.handlePhotoUpload(field)}
            />
          </div>
        );
      case UPLOAD_FILE:
        return (
          <div key={field.name} className={fieldClasses}>
            <AddFileField
              label={formLabels[field.translationKey || field.name].label}
              placeholder={
                formLabels[field.translationKey || field.name].placeholder
              }
              uploadedFiles={values[field.name] || null}
              isMultiple={field.isMultiple}
              types={field.types}
              hasError={!!errors[field.name]}
              errorMessage={validationMessages[errors[field.name]]}
              onAddFile={this.handleFileUpload(field)}
              onRejectFile={this.handleFileReject(field)}
            />
          </div>
        );
      case CHECKBOX:
        if (field.isToggleable) {
          return (
            <Fade
              key={`checkbox_field_${field?.fieldGroup || ''}${field.name}`}
              in={field.shouldRender(values)}
              timeout={{
                enter: DEFAULT_FADE_IN,
                exit: field.shouldFadeOutInstant ? 0 : DEFAULT_FADE_OUT,
              }}
              unmountOnExit
            >
              <div key={field.name} className={fieldClasses}>
                <CustomCheckbox
                  className={field.className}
                  customRootClass={classNames({
                    [classes.checkbox]: field.shouldTopAlign,
                  })}
                  labelTextClass={classes.checkboxLabel}
                  isChecked={field.isReverse ? !fieldValue : !!fieldValue}
                  label={formLabels[field.translationKey || field.name].label}
                  labelIcon={LabelIcon}
                  endLabelIcon={field.endLabelIcon}
                  labelText={
                    formLabels[field.translationKey || field.name].labelText
                  }
                  labelHelp={
                    formLabels[field.translationKey || field.name].labelHelp
                  }
                  endLabelIconHelp={
                    formLabels[field.translationKey || field.name]
                      .endLabelIconHelp
                  }
                  hasError={hasError}
                  errorMessage={errorMessage}
                  isControlled={field.isControlled}
                  isHighlightedLabel={field.isHighlightedLabel}
                  disabledTooltipText={
                    formLabels[field.translationKey || field.name]
                      ?.disabledTooltip
                  }
                  disabled={
                    field.isDisabled ||
                    (field.isDisabledCheck
                      ? field.isDisabledCheck(values)
                      : undefined)
                  }
                  smallText={field.smallText}
                  onChange={this.handleCheckboxChange(field)}
                />
              </div>
            </Fade>
          );
        }
        return (
          <div key={field.name} className={fieldClasses}>
            <CustomCheckbox
              className={field.className}
              customRootClass={classNames({
                [classes.checkbox]: field.shouldTopAlign,
              })}
              labelTextClass={classes.checkboxLabel}
              isChecked={field.isReverse ? !fieldValue : !!fieldValue}
              label={formLabels[field.translationKey || field.name].label}
              labelIcon={LabelIcon}
              endLabelIcon={field.endLabelIcon}
              labelText={
                formLabels[field.translationKey || field.name].labelText
              }
              labelHelp={
                formLabels[field.translationKey || field.name].labelHelp
              }
              endLabelIconHelp={
                formLabels[field.translationKey || field.name].endLabelIconHelp
              }
              hasError={hasError}
              errorMessage={errorMessage}
              isControlled={field.isControlled}
              isHighlightedLabel={field.isHighlightedLabel}
              disabledTooltipText={
                formLabels[field.translationKey || field.name]?.disabledTooltip
              }
              disabled={
                field.isDisabled ||
                (field.isDisabledCheck
                  ? field.isDisabledCheck(values)
                  : undefined)
              }
              smallText={field.smallText}
              onChange={this.handleCheckboxChange(field)}
            />
          </div>
        );
      case CHECKBOX_GROUP:
        if (field.isToggleable) {
          return (
            <Fade
              key={`checkbox_group_${field?.fieldGroup || ''}${field.name}`}
              in={field.shouldRender(values)}
              timeout={{
                enter: DEFAULT_FADE_IN,
                exit: field.shouldFadeOutInstant ? 0 : DEFAULT_FADE_OUT,
              }}
              unmountOnExit
            >
              <div key={field.name} className={fieldClasses}>
                <CheckboxGroup
                  label={formLabels[field.translationKey || field.name].label}
                  labelHelp={
                    formLabels[field.translationKey || field.name]?.labelHelp
                  }
                  hasScroll={field.hasScroll}
                  checkedItems={fieldValue}
                  checkboxItems={
                    field.getItems ? field.getItems(values, formLabels) : []
                  }
                  checkboxContentClass={field.checkboxContentClass}
                  customRootClass={field.customRootClass}
                  onChange={this.handleCheckboxGroupChange(field)}
                />
              </div>
            </Fade>
          );
        }
        return (
          <div key={field.name} className={fieldClasses}>
            <CheckboxGroup
              label={formLabels[field.translationKey || field.name].label}
              labelHelp={
                formLabels[field.translationKey || field.name]?.labelHelp
              }
              hasScroll={field.hasScroll}
              checkedItems={fieldValue}
              checkboxItems={
                field.getItems ? field.getItems(values, formLabels) : []
              }
              checkboxContentClass={field.checkboxContentClass}
              customRootClass={field.customRootClass}
              onChange={this.handleCheckboxGroupChange(field)}
            />
          </div>
        );
      case RADIO_GROUP:
        return (
          <div key={`radio_group_${field.name}`} className={fieldClasses}>
            <CustomRadioGroup
              label={formLabels[field.translationKey || field.name]?.label}
              value={values[field.name]}
              isDisabled={field.isDisabled}
              options={field.options}
              customLabelClass={field.customLabelClass}
              customLabelWrapperClass={field.customLabelWrapperClass}
              customOptionLabelWrapperClass={
                field.customOptionLabelWrapperClass
              }
              customRadioClass={field.customRadioClass}
              onChange={this.handleRadioGroupChange(field)}
            />
          </div>
        );
      case DATE_PICKER:
        if (field.isToggleable) {
          return (
            <Fade
              key={`date_picker_${field?.fieldGroup || ''}${field.name}`}
              in={field.shouldRender(values)}
              timeout={{
                enter: DEFAULT_FADE_IN,
                exit: field.shouldFadeOutInstant ? 0 : DEFAULT_FADE_OUT,
              }}
              unmountOnExit
            >
              <div className={fieldClasses}>
                <CustomDatePicker
                  name={field.name}
                  label={formLabels[field.translationKey || field.name]?.label}
                  placeholder={
                    formLabels[field.translationKey || field.name]?.placeholder
                  }
                  tooltipInfo={
                    formLabels[field.translationKey || field.name]?.tooltipInfo
                  }
                  value={fieldValue || null}
                  format={field.selectedDateFormat}
                  error={hasError}
                  errorMessage={errorMessage}
                  required={field.required}
                  disableFuture={field.disableFuture}
                  disablePast={field.isPastDisabled}
                  disableToolbar={field.isToolbarDisabled}
                  shouldDisableDate={field.shouldDisableDate}
                  disabled={
                    field.isDisabled ||
                    (field.isDisabledCheck
                      ? field.isDisabledCheck(values)
                      : undefined)
                  }
                  minDate={
                    field.getMinDate
                      ? field.getMinDate(
                          values[field.name],
                          values,
                          initialValues
                        )
                      : undefined
                  }
                  maxDate={
                    field.getMaxDate
                      ? field.getMaxDate(
                          values[field.name],
                          values,
                          initialValues
                        )
                      : undefined
                  }
                  onChange={this.handleDateTimeChange(field)}
                  fullWidth
                  autoOk
                  isIconOnStart
                />
              </div>
            </Fade>
          );
        }
        return (
          <div
            key={`date_picker_${field?.fieldGroup || ''}${field.name}`}
            className={fieldClasses}
          >
            <CustomDatePicker
              name={field.name}
              label={formLabels[field.translationKey || field.name]?.label}
              placeholder={
                formLabels[field.translationKey || field.name]?.placeholder
              }
              tooltipInfo={
                formLabels[field.translationKey || field.name]?.tooltipInfo
              }
              value={fieldValue || null}
              format={field.selectedDateFormat}
              error={hasError}
              errorMessage={errorMessage}
              required={field.required}
              disableFuture={field.disableFuture}
              disablePast={field.isPastDisabled}
              disableToolbar={field.isToolbarDisabled}
              shouldDisableDate={field.shouldDisableDate}
              disabled={
                field.isDisabled ||
                (field.isDisabledCheck
                  ? field.isDisabledCheck(values)
                  : undefined)
              }
              minDate={
                field.getMinDate
                  ? field.getMinDate(values[field.name], values, initialValues)
                  : undefined
              }
              maxDate={
                field.getMaxDate
                  ? field.getMaxDate(values[field.name], values, initialValues)
                  : undefined
              }
              onChange={this.handleDateTimeChange(field)}
              fullWidth
              autoOk
              isIconOnStart
            />
          </div>
        );

      case MULTIPLE_DATE_PICKER:
        return (
          <div
            key={`multiple_date_picker_${field?.fieldGroup || ''}${field.name}`}
            className={classNames(classes.field, field.fieldWrapperClass)}
          >
            <MultipleDatePicker
              label={formLabels[field.translationKey || field.name]?.label}
              placeholder={
                formLabels[field.translationKey || field.name]?.placeholder
              }
              tooltipInfo={
                formLabels[field.translationKey || field.name]?.tooltipInfo
              }
              addDateButtonLabel={
                formLabels[field.translationKey || field.name]
                  ?.addDateButtonLabel
              }
              value={fieldValue || []}
              minDate={
                field.getMinDate
                  ? field.getMinDate(values[field.name], values, initialValues)
                  : undefined
              }
              maxDate={
                field.getMaxDate
                  ? field.getMaxDate(values[field.name], values, initialValues)
                  : undefined
              }
              errors={errors[field.name]}
              errorMessages={validationMessages}
              onChange={this.handleDateTimeChange(field)}
              isFullWidth
              autoOk
              isIconOnStart
            />
          </div>
        );
      case RICH_TEXT_EDITOR:
        if (field.isToggleable) {
          return (
            <Fade
              key={field.name}
              in={field.shouldRender(values)}
              timeout={{
                enter: DEFAULT_FADE_IN,
                exit: field.shouldFadeOutInstant ? 0 : DEFAULT_FADE_OUT,
              }}
              unmountOnExit
            >
              <div
                className={classNames(
                  {
                    [classes.field]:
                      !field.hasDivider && !field.shouldRemoveFieldSpacing,
                    [classes.divider]: field.hasDivider,
                  },
                  field.fieldWrapperClass
                )}
              >
                <CustomRichTextEditor
                  customLabelWrapperClass={field.labelWrapperClass}
                  customLabelClass={field.labelClass}
                  customEditorClass={classNames(
                    classes.textEditor,
                    field.customRichTextEditorClass
                  )}
                  customErrorClass={classNames(
                    classes.privateNoteError,
                    field.privateNoteErrorClass
                  )}
                  label={formLabels[field.translationKey || field.name]?.label}
                  placeholder={
                    formLabels[field.translationKey || field.name]?.placeholder
                  }
                  labelTooltip={
                    field.labelTooltip ||
                    formLabels[field.translationKey]?.labelTooltip
                  }
                  value={fieldValue}
                  hasError={hasError}
                  isReadOnly={field.isReadOnly}
                  errorMessage={errorMessage}
                  modules={field.richTextModules}
                  formats={field.richTextFormats}
                  onUpdateText={updatedValue => {
                    this.handleValidateSingleField(
                      field,
                      updatedValue,
                      initialValues[field]
                    );
                    setFieldValue(field.name, updatedValue);
                  }}
                />
              </div>
            </Fade>
          );
        }
        return (
          <div
            key={field.name}
            className={classNames(
              {
                [classes.field]:
                  !field.hasDivider && !field.shouldRemoveFieldSpacing,
                [classes.divider]: field.hasDivider,
              },
              field.fieldWrapperClass
            )}
          >
            <CustomRichTextEditor
              customLabelWrapperClass={field.labelWrapperClass}
              customLabelClass={field.labelClass}
              customEditorClass={classNames(
                classes.textEditor,
                field.customRichTextEditorClass
              )}
              customErrorClass={classNames(
                classes.privateNoteError,
                field.privateNoteErrorClass
              )}
              label={formLabels[field.translationKey || field.name]?.label}
              placeholder={
                formLabels[field.translationKey || field.name]?.placeholder
              }
              labelTooltip={
                field.labelTooltip ||
                formLabels[field.translationKey]?.labelTooltip
              }
              value={fieldValue}
              hasError={hasError}
              isReadOnly={field.isReadOnly}
              errorMessage={errorMessage}
              modules={field.richTextModules}
              formats={field.richTextFormats}
              onUpdateText={updatedValue => {
                this.handleValidateSingleField(
                  field,
                  updatedValue,
                  initialValues[field]
                );
                setFieldValue(field.name, updatedValue);
              }}
              onSelectionChange={
                field.syncA4PreviewPage
                  ? this.handleA4PreviewPageSync
                  : undefined
              }
            />
          </div>
        );
      case SUBTITLE:
        if (field.isToggleable) {
          return (
            <Fade
              key={field.name}
              in={field.shouldRender(values)}
              timeout={{
                enter: DEFAULT_FADE_IN,
                exit: field.shouldFadeOutInstant ? 0 : DEFAULT_FADE_OUT,
              }}
              unmountOnExit
            >
              <div key={field.name} className={fieldClasses}>
                <Typography variant={field?.variant || 'h5'}>
                  {formLabels[field.name]?.label || field?.label}
                </Typography>
              </div>
            </Fade>
          );
        }
        return (
          <div key={field.name} className={fieldClasses}>
            <Typography variant={field?.variant || 'h5'}>
              {formLabels[field.name]?.label || field?.label}
            </Typography>
          </div>
        );
      case SUBTITLE_WITH_TOOLTIP:
        if (field.isToggleable) {
          return (
            <Fade
              key={field.name}
              in={field.shouldRender(values)}
              timeout={{
                enter: DEFAULT_FADE_IN,
                exit: field.shouldFadeOutInstant ? 0 : DEFAULT_FADE_OUT,
              }}
              unmountOnExit
            >
              <div key={field.name} className={fieldClasses}>
                <TextBoxWithTooltip
                  variant={field?.variant || 'h5'}
                  text={formLabels[field.name]?.label || field?.label}
                />
              </div>
            </Fade>
          );
        }
        return (
          <div key={field.name} className={fieldClasses}>
            <TextBoxWithTooltip
              variant={field?.variant || 'h5'}
              text={formLabels[field.name]?.label || field?.label}
            />
          </div>
        );
      case REVIEWS:
        return (
          <div
            key={field.name}
            className={classNames(
              {
                [classes.field]: !field.hasDivider,
                [classes.divider]: field.hasDivider,
              },
              field.fieldWrapperClass
            )}
          >
            <ReviewsField
              translations={formLabels[field.translationKey || field.name]}
              reviews={this.props[field.optionsKey]}
              questionnaires={this.props?.questionnaires}
              survey={this.props?.survey}
              selectedUsers={this.props?.selectedUsers}
              value={fieldValue}
              errors={errors[field.name]}
              errorMessages={validationMessages}
              onChange={this.handleReviewChange(field)}
            />
          </div>
        );
      case TIME_PICKER:
        if (field.isToggleable) {
          return (
            <Fade
              key={`time_picker_${field?.fieldGroup || ''}${field.name}`}
              in={field.shouldRender(values)}
              timeout={{
                enter: DEFAULT_FADE_IN,
                exit: field.shouldFadeOutInstant ? 0 : DEFAULT_FADE_OUT,
              }}
              unmountOnExit
            >
              <div
                className={classNames(
                  {
                    [classes.field]: !field.hasDivider,
                    [classes.divider]: field.hasDivider,
                  },
                  field.fieldWrapperClass
                )}
              >
                <CustomTimePicker
                  customRootClass={field.customRootClass}
                  label={formLabels[field.translationKey || field.name]?.label}
                  selectedTime={fieldValue || null}
                  disabled={
                    field.isDisabled ||
                    (field.isDisabledCheck
                      ? field.isDisabledCheck(values)
                      : undefined)
                  }
                  minuteStep={field.minuteStep}
                  error={hasError}
                  errorMessage={field.shouldShowError ? errorMessage : ''}
                  onChange={this.handleDateTimeChange(field)}
                />
              </div>
            </Fade>
          );
        }
        return (
          <div
            key={`time_picker_${field?.fieldGroup || ''}${field.name}`}
            className={classNames(
              {
                [classes.field]: !field.hasDivider,
                [classes.divider]: field.hasDivider,
              },
              field.fieldWrapperClass
            )}
          >
            <CustomTimePicker
              customRootClass={field.customRootClass}
              label={formLabels[field.translationKey || field.name]?.label}
              selectedTime={fieldValue || null}
              disabled={
                field.isDisabled ||
                (field.isDisabledCheck
                  ? field.isDisabledCheck(values)
                  : undefined)
              }
              minuteStep={field.minuteStep}
              error={hasError}
              errorMessage={errorMessage}
              onChange={this.handleDateTimeChange(field)}
            />
          </div>
        );
      case READ_ONLY:
        if (field.isToggleable) {
          return (
            <Fade
              key={`read_only_${field.name}`}
              in={field.shouldRender(values)}
              timeout={{
                enter: DEFAULT_FADE_IN,
                exit: field.shouldFadeOutInstant ? 0 : DEFAULT_FADE_OUT,
              }}
              unmountOnExit
            >
              <div
                className={classNames(classes.field, field.fieldWrapperClass)}
              >
                <FormHelperText>
                  {formLabels[field.translationKey || field.name].label}
                </FormHelperText>
                <Typography
                  className={classes.readOnly}
                  variant={field.variant || 'subtitle2'}
                  component="div"
                >
                  {values[field.content]}
                </Typography>
              </div>
            </Fade>
          );
        }
        return (
          <div
            key={field.name}
            className={classNames(classes.field, field.fieldWrapperClass)}
          >
            <FormHelperText>
              {formLabels[field.translationKey || field.name].label}
            </FormHelperText>
            <Typography
              className={classes.readOnly}
              variant={field.variant || 'subtitle2'}
              component="div"
            >
              {values[field.content]}
            </Typography>
          </div>
        );
      case PEOPLE_PICKER:
        if (field.isToggleable) {
          return (
            <Fade
              key={`${field.name}_${field?.translationKey || ''}`}
              in={field.shouldRender(values)}
              timeout={{
                enter: DEFAULT_FADE_IN,
                exit: field.shouldFadeOutInstant ? 0 : DEFAULT_FADE_OUT,
              }}
              unmountOnExit
            >
              <div className={fieldClasses}>
                <PeoplePicker
                  parentNode={contentNode}
                  className={field.className}
                  selectedUsersScrollYClass={field.selectedUsersScrollYClass}
                  translations={formLabels[field.translationKey || field.name]}
                  preselectedUsers={fieldValue || []}
                  parentOffset={field.parentOffset}
                  options={this.getFieldOptions(field)}
                  blacklistedUsers={getObjectToNumberArray(
                    values?.[field.parent] || []
                  )}
                  formattedUsers={getFormattedUsers(
                    this.getFieldOptions(field),
                    true
                  )}
                  errorMessage={validationMessages?.[errors[field.name]]}
                  hasError={hasError}
                  hasSelectAll={field.hasSelectAll}
                  isDisabled={field.isDisabled}
                  showSelection={field.showSelection}
                  showDirectReports={field.showDirectReports}
                  shouldSortAlphabetically={field.shouldSortAlphabetically}
                  shouldCalculateSelectionHeight={
                    field.shouldCalculateSelectionHeight
                  }
                  isAsync={field.isAsync}
                  shouldHandleTypeLabel={field.shouldHandleTypeLabel}
                  onSelect={selectedValues =>
                    this.handleAutocompleteSelect(
                      field,
                      selectedValues,
                      field.dependants
                    )
                  }
                  handleTypeLabel={field.handleTypeLabel}
                />
              </div>
            </Fade>
          );
        }
        return (
          <div key={field.name} className={fieldClasses}>
            <PeoplePicker
              parentNode={contentNode}
              className={field.className}
              selectedUsersScrollYClass={field.selectedUsersScrollYClass}
              translations={formLabels[field.translationKey || field.name]}
              preselectedUsers={fieldValue || []}
              parentOffset={field.parentOffset}
              options={this.getFieldOptions(field)}
              blacklistedUsers={getObjectToNumberArray(
                values?.[field.parent] || []
              )}
              formattedUsers={getFormattedUsers(
                this.getFieldOptions(field),
                true
              )}
              errorMessage={validationMessages?.[errors[field.name]]}
              hasError={hasError}
              hasSelectAll={field.hasSelectAll}
              isDisabled={field.isDisabled}
              showSelection={field.showSelection}
              showDirectReports={field.showDirectReports}
              shouldSortAlphabetically={field.shouldSortAlphabetically}
              shouldCalculateSelectionHeight={
                field.shouldCalculateSelectionHeight
              }
              isAsync={field.isAsync}
              shouldHandleTypeLabel={field.shouldHandleTypeLabel}
              onSelect={selectedValues =>
                this.handleAutocompleteSelect(
                  field,
                  selectedValues,
                  field.dependants
                )
              }
              handleTypeLabel={field.handleTypeLabel}
            />
          </div>
        );
      case MANAGE_USERS_LIST:
        if (field.isToggleable) {
          return (
            <Fade
              key={`${field.name}_${field?.translationKey || ''}`}
              in={field.shouldRender(values)}
              timeout={{
                enter: DEFAULT_FADE_IN,
                exit: field.shouldFadeOutInstant ? 0 : DEFAULT_FADE_OUT,
              }}
              unmountOnExit
            >
              <div className={fieldClasses}>
                <ManageUsersList
                  parentNode={contentNode}
                  className={field.className}
                  customErrorClass={field.customErrorClass}
                  selectedItemsScrollYClass={field.selectedItemsScrollYClass}
                  translations={formLabels[field.translationKey || field.name]}
                  values={fieldValue || []}
                  parentOffset={field.parentOffset}
                  hasError={hasError}
                  errorMessage={validationMessages?.[errors[field.name]]}
                  showSelection={field.showSelection}
                  hasDelete={field.hasDelete}
                  shouldCalculateSelectionHeight={
                    field.shouldCalculateSelectionHeight
                  }
                  shouldHandleTypeLabel={field.shouldHandleTypeLabel}
                  onSelect={results =>
                    this.handleAutocompleteSelect(field, results)
                  }
                  handleTypeLabel={field.handleTypeLabel}
                />
              </div>
            </Fade>
          );
        }
        return (
          <div key={field.name} className={fieldClasses}>
            <ManageUsersList
              parentNode={contentNode}
              className={field.className}
              customErrorClass={field.customErrorClass}
              selectedItemsScrollYClass={field.selectedItemsScrollYClass}
              translations={formLabels[field.translationKey || field.name]}
              values={fieldValue || []}
              parentOffset={field.parentOffset}
              hasError={hasError}
              errorMessage={validationMessages?.[errors[field.name]]}
              hasDelete={field.hasDelete}
              showSelection={field.showSelection}
              shouldCalculateSelectionHeight={
                field.shouldCalculateSelectionHeight
              }
              shouldHandleTypeLabel={field.shouldHandleTypeLabel}
              onSelect={results =>
                this.handleAutocompleteSelect(field, results)
              }
              handleTypeLabel={field.handleTypeLabel}
            />
          </div>
        );
      case PREVIEW:
        return (
          <PreviewField
            key={field.name}
            translations={formLabels[field.translationKey || field.name]}
            data={values[field.previewKey]}
            isReadOnly={field.isReadOnly}
            component={field.component}
            isExpandedOnFirstOpen={this.state.isExpandedOnFirstOpen}
            handleIsExpandedOnFirstOpen={value =>
              this.setState({ isExpandedOnFirstOpen: value })
            }
            isExpandedOnFirstOpenFramework={
              this.state.isExpandedOnFirstOpenFramework
            }
            handleIsExpandedOnFirstOpenFramework={value =>
              this.setState({ isExpandedOnFirstOpenFramework: value })
            }
          />
        );
      case A4_DOCUMENT_PREVIEW:
        return (
          <div
            key={field.name}
            className={classNames(
              {
                [classes.field]: !field.shouldRemoveFieldSpacing,
              },
              field.fieldWrapperClass
            )}
          >
            <A4DocumentPreview
              content={values[field.previewKey] || ''}
              activePageIndex={
                field.followEditorPage ? this.state.a4PreviewActivePage : 0
              }
              className={field.className}
            />
          </div>
        );
      case SURVEY_PREVIEW:
        if (field.isToggleable) {
          return (
            <Fade
              key={field.name}
              className={classes.columnFieldCenter}
              in={field.shouldRender(values)}
              timeout={{
                enter: DEFAULT_FADE_IN,
                exit: field.shouldFadeOutInstant ? 0 : DEFAULT_FADE_OUT,
              }}
              unmountOnExit
            >
              <SurveyPreview
                key={field.name}
                translations={formLabels[field.translationKey || field.name]}
                attribute={this.props[field.reviewKey]}
                onGetReview={this.props[field.getReviewKey]}
                onClearReview={this.props[field.clearReviewKey]}
                values={values}
              />
            </Fade>
          );
        }
        return (
          <SurveyPreview
            key={field.name}
            translations={formLabels[field.translationKey || field.name]}
            attribute={this.props[field.reviewKey]}
            onGetReview={this.props[field.getReviewKey]}
            onClearReview={this.props[field.clearReviewKey]}
            values={values}
          />
        );
      case QUESTIONNAIRE_QUESTIONS:
        if (field.isToggleable) {
          return (
            <Fade
              key={field.name}
              className={classes.columnFieldFill}
              in={field.shouldRender(values)}
              timeout={{
                enter: DEFAULT_FADE_IN,
                exit: field.shouldFadeOutInstant ? 0 : DEFAULT_FADE_OUT,
              }}
              unmountOnExit
            >
              <QuestionnaireQuestionsField
                translations={formLabels[field.translationKey || field.name]}
                values={values}
                questionnaire={fieldValue || EMPTY_QUESTIONNAIRE}
                hasError={errors[field.name]}
                onChange={this.handleQuestionnaireQuestionsChange(field)}
                onValidate={this.handleQuestionnaireValidate(field)}
                onRegisterFlush={this.handleRegisterQuestionnaireFlush}
              />
            </Fade>
          );
        }
        return (
          <QuestionnaireQuestionsField
            key={field.name}
            translations={formLabels[field.translationKey || field.name]}
            values={values}
            questionnaire={fieldValue || EMPTY_QUESTIONNAIRE}
            hasError={errors[field.name]}
            onChange={this.handleQuestionnaireQuestionsChange(field)}
            onValidate={this.handleQuestionnaireValidate(field)}
            onRegisterFlush={this.handleRegisterQuestionnaireFlush}
          />
        );
      case ADD_COURSES:
        if (field.isToggleable) {
          return (
            <Fade
              key={field.name}
              in={field.shouldRender(values)}
              timeout={{
                enter: DEFAULT_FADE_IN,
                exit: field.shouldFadeOutInstant ? 0 : DEFAULT_FADE_OUT,
              }}
              unmountOnExit
            >
              <div
                className={classNames(classes.field, field.fieldWrapperClass)}
              >
                <AddCoursesField
                  translations={formLabels[field.translationKey || field.name]}
                  selectedCourses={fieldValue}
                  courses={this.getFieldOptions(field)}
                  getCourses={field.getCourses}
                  getEnrolledUsers={field.getCourseEnrolledUsers}
                  onCoursesSearch={field.onCoursesSearch}
                  onChange={this.handleAddCourse(field)}
                />
              </div>
            </Fade>
          );
        }
        return (
          <AddCoursesField
            key={field.name}
            className={classNames(classes.field, field.fieldWrapperClass)}
            translations={formLabels[field.translationKey || field.name]}
            selectedCourses={fieldValue}
            courses={this.getFieldOptions(field)}
            getCourses={field.getCourses}
            getEnrolledUsers={field.getCourseEnrolledUsers}
            onCoursesSearch={field.onCoursesSearch}
            onChange={this.handleAddCourse(field)}
          />
        );
      case ADD_ADDITIONAL_ROLES_FIELD:
        return (
          <AddAdditionalRolesField
            key={field.name}
            translations={formLabels[field.translationKey || field.name]}
            roles={fieldValue}
            hasError={hasError}
            errors={errors?.[field.name]}
            onChange={this.handleMultipleValuesFieldChange(field)}
          />
        );
      case ATTRIBUTES_QUESTIONS_FORM:
        return (
          <AttributeQuestionsForm
            key={field.name}
            field={field}
            values={values}
            initialValues={initialValues}
            translations={translations}
            setDialogVisibility={setDialogVisibility}
            dialogs={dialogs}
            hasError={errors[field.attributeQuestions.name]}
            updateAttributeQuestions={this.handleAttributeQuestionsChange(
              field
            )}
          />
        );
      case ADD_USERS_TABLE:
        return (
          <div key={field.name} className={classNames(field.fieldWrapperClass)}>
            <AddUsersTableField
              translations={translations}
              data={field}
              organizationUser={this.props[field.organizationUserKey]}
              users={fieldValue}
              errors={errors[field.name]}
              onChange={this.handleAddUsersChange(field)}
            />
          </div>
        );
      case MEETING_IMPRESSION:
        return (
          <div
            key={field.name}
            className={classNames(
              {
                [classes.field]:
                  !field.hasDivider && !field.shouldRemoveFieldSpacing,
                [classes.divider]: field.hasDivider,
              },
              field.fieldWrapperClass
            )}
          >
            <Impressions
              translations={formLabels[field.translationKey || field.name]}
              label={formLabels[field.translationKey || field.name]?.label}
              value={fieldValue}
              isFutureMeeting={field.isFutureMeeting(values)}
              onSelectHandler={this.handleChangeSelectValue(field)}
            />
          </div>
        );
      case MEETING_STATUS:
        return (
          <div
            key={field.name}
            className={classNames(
              classes.meetingStatus,
              field.fieldWrapperClass
            )}
          >
            <OneOnOneTimeIndicator
              labels={formLabels[field.translationKey || field.name]?.labels}
              meetingTime={fieldValue}
              hasImpression={values[field.parent] !== null}
            />
          </div>
        );
      case MEETING_AGENDA:
        return (
          <div key={field.name} className={fieldClasses}>
            <MeetingTopics
              label={formLabels[field.translationKey || field.name]?.label}
              placeholder={
                formLabels[field.translationKey || field.name].placeholder
              }
              topics={field.topics}
              agenda={fieldValue}
              errorType={errors[field.name]}
              hasError={hasError}
              errorMessage={errorMessage}
              initialCustomTopic={initialValues[field.name]?.customTopic}
              onChange={this.handleMeetingTopicsChange(field)}
            />
          </div>
        );
      case FILTERS_SETUP:
        return (
          <div key={`${field.name}_${field.id}`} className={fieldClasses}>
            <FiltersSetup
              label={formLabels[field.translationKey || field.name]?.label}
              subtext={formLabels[field.translationKey || field.name]?.subtext}
              helpText={
                formLabels[field.translationKey || field.name]?.helpText
              }
              allPlacements={field.placements}
              placements={fieldValue}
              onChange={this.handleRecommendedPlacementChange(field)}
            />
          </div>
        );
      case COLLAPSABLE_FILTERS:
        if (field.isToggleable) {
          return (
            <Fade
              key={`${field.name}_${field?.translationKey || ''}`}
              in={field.shouldRender(values)}
              timeout={{
                enter: DEFAULT_FADE_IN,
                exit: field.shouldFadeOutInstant ? 0 : DEFAULT_FADE_OUT,
              }}
              unmountOnExit
            >
              <div className={fieldClasses}>
                <CollapsableFilters
                  parentNode={contentNode}
                  className={field.className}
                  selectedItemsScrollYClass={field.selectedItemsScrollYClass}
                  selectedFilters={fieldValue.selected}
                  translations={formLabels[field.translationKey || field.name]}
                  values={fieldValue?.filteredValues || []}
                  filters={field.filters}
                  parentOffset={field.parentOffset}
                  errorMessage={validationMessages?.[errors[field.name]]}
                  hasError={hasError}
                  isDisabled={field.isDisabled || fieldValue?.isDisabled}
                  showSelection={field.showSelection}
                  shouldCalculateSelectionHeight={
                    field.shouldCalculateSelectionHeight
                  }
                  onApplyFilters={this.handleFilterResults(field)}
                />
              </div>
            </Fade>
          );
        }
        return (
          <div key={field.name} className={fieldClasses}>
            <CollapsableFilters
              parentNode={contentNode}
              className={field.className}
              selectedItemsScrollYClass={field.selectedItemsScrollYClass}
              selectedFilters={fieldValue.selected}
              translations={formLabels[field.translationKey || field.name]}
              values={fieldValue?.filteredValues || []}
              filters={field.filters}
              parentOffset={field.parentOffset}
              errorMessage={validationMessages?.[errors[field.name]]}
              hasError={hasError}
              isDisabled={field.isDisabled || fieldValue?.isDisabled}
              showSelection={field.showSelection}
              shouldCalculateSelectionHeight={
                field.shouldCalculateSelectionHeight
              }
              onApplyFilters={this.handleFilterResults(field)}
            />
          </div>
        );
      case CREATABLE_TAGS:
        return (
          <div key={field.name} className={fieldClasses}>
            <CreatableTagsField
              label={formLabels[field.translationKey || field.name]?.label}
              placeholder={
                formLabels[field.translationKey || field.name].placeholder
              }
              startAdornment={
                field.startAdornment ? field.startAdornment(values) : undefined
              }
              hasError={hasError}
              errorMessage={errorMessage}
              initialTags={initialValues[field.name]}
              tags={fieldValue}
              category={field.category}
              field={field}
              onValidate={this.handleValidateSingleField}
              onSetError={(fieldName, error) => setFieldError(fieldName, error)}
              onChange={value => setFieldValue(field.name, value)}
            />
          </div>
        );
      case CREATABLE_TEXTS:
        if (field.isToggleable) {
          return (
            <Fade
              key={`${field.name}_${field?.translationKey || ''}`}
              in={field.shouldRender(values)}
              timeout={{
                enter: DEFAULT_FADE_IN,
                exit: field.shouldFadeOutInstant ? 0 : DEFAULT_FADE_OUT,
              }}
              unmountOnExit
            >
              <div className={fieldClasses}>
                <CreatableTextsField
                  translations={formLabels[field.translationKey || field.name]}
                  texts={fieldValue}
                  field={field.textField}
                  errors={errors?.[field.name]}
                  onChange={this.onChangeMultipleField(field)}
                />
              </div>
            </Fade>
          );
        }
        return (
          <div key={field.name} className={fieldClasses}>
            <CreatableTextsField
              translations={formLabels[field.translationKey || field.name]}
              texts={fieldValue}
              field={field.textField}
              errors={errors?.[field.name]}
              onChange={this.onChangeMultipleField(field)}
            />
          </div>
        );
      case SWITCH: {
        const switchLabels = formLabels[field.translationKey || field.name];
        let switchStatusLabel;
        if (field.showStatusLabel) {
          switchStatusLabel = fieldValue
            ? switchLabels?.active
            : switchLabels?.inactive;
        }

        if (field.isToggleable) {
          return (
            <Fade
              key={`switch_field_${field?.fieldGroup || ''}${field.name}`}
              in={field.shouldRender(values)}
              timeout={{
                enter: DEFAULT_FADE_IN,
                exit: field.shouldFadeOutInstant ? 0 : DEFAULT_FADE_OUT,
              }}
              unmountOnExit
            >
              <div key={field.name} className={fieldClasses}>
                <CustomSwitch
                  className={field.className}
                  labelClass={field.labelClass}
                  label={field.shouldRemoveLabel ? '' : switchLabels?.label}
                  description={switchLabels?.description}
                  tooltipInfo={switchLabels?.tooltipInfo}
                  tooltipText={
                    field.getTooltipText
                      ? field.getTooltipText(values, switchLabels)
                      : switchLabels?.tooltipText
                  }
                  disabledTooltipText={switchLabels?.disabledTooltip}
                  isStacked={!!field.showStatusLabel}
                  statusLabel={switchStatusLabel}
                  isHighlightedLabel={field.isHighlightedLabel}
                  isChecked={!!fieldValue}
                  isDisabled={
                    field.isDisabled ||
                    (field.isDisabledCheck
                      ? field.isDisabledCheck(values)
                      : undefined)
                  }
                  onChange={this.handleCheckboxChange(field)}
                />
              </div>
            </Fade>
          );
        }
        return (
          <div key={field.name} className={fieldClasses}>
            <CustomSwitch
              className={field.className}
              labelClass={field.labelClass}
              label={field.shouldRemoveLabel ? '' : switchLabels?.label}
              description={switchLabels?.description}
              tooltipInfo={switchLabels?.tooltipInfo}
              tooltipText={
                field.getTooltipText
                  ? field.getTooltipText(values, switchLabels)
                  : switchLabels?.tooltipText
              }
              disabledTooltipText={switchLabels?.disabledTooltip}
              isStacked={!!field.showStatusLabel}
              statusLabel={switchStatusLabel}
              isHighlightedLabel={field.isHighlightedLabel}
              isChecked={!!fieldValue}
              isDisabled={
                field.isDisabled ||
                (field.isDisabledCheck
                  ? field.isDisabledCheck(values)
                  : undefined)
              }
              onChange={this.handleCheckboxChange(field)}
            />
          </div>
        );
      }
      case TASKS:
        return (
          <Fade
            key={`tasks_${field.name}`}
            in={field.shouldRender && field.shouldRender(values)}
            timeout={{
              enter: DEFAULT_FADE_IN,
              exit: field.shouldFadeOutInstant ? 0 : DEFAULT_FADE_OUT,
            }}
            unmountOnExit
          >
            <div key={field.name} className={fieldClasses}>
              <TasksField
                translations={formLabels[field.translationKey || field.name]}
                tasks={fieldValue}
                allUsers={this.props[field.optionsKey]}
                checkCanManageTask={field.checkCanManageTask}
                error={errors[field.name]}
                onChange={this.handleMultipleValuesFieldChange(field)}
              />
            </div>
          </Fade>
        );
      case DATE_RANGE_WITH_TEXT_LIST:
        return (
          <div key={field.name} className={fieldClasses}>
            <DateRangeWithTextFields
              translations={formLabels[field.translationKey || field.name]}
              values={fieldValue}
              initialValues={field.initialValues}
              fieldTypes={field.fieldTypes}
              error={errors[field.name]}
              isDisabled={
                field.isDisabled ||
                (field.isDisabledCheck
                  ? field.isDisabledCheck(values)
                  : undefined)
              }
              onChange={this.handleFildsChange(field)}
            />
          </div>
        );
      case SALARY_RANGE:
        return (
          <div key={field.name} className={fieldClasses}>
            <SalaryRanges
              translations={formLabels[field.translationKey || field.name]}
              values={fieldValue}
              currency={field.currency}
              fieldTypes={field.fieldTypes}
              error={errors[field.name]}
              onChange={this.handleFildsChange(field)}
            />
          </div>
        );
      case TEXT_LIST:
        return (
          <div key={field.name} className={fieldClasses}>
            <TextFields
              translations={formLabels[field.translationKey || field.name]}
              values={fieldValue}
              initialValues={field.initialValues}
              fieldTypes={field.fieldTypes}
              error={errors[field.name]}
              isDisabled={
                field.isDisabled ||
                (field.isDisabledCheck
                  ? field.isDisabledCheck(values)
                  : undefined)
              }
              onChange={this.handleFildsChange(field)}
            />
          </div>
        );
      case DOCUMENT_TYPE_FIELDS:
        return (
          <div key={field.name} className={fieldClasses}>
            <DocumentTypeFields
              translations={formLabels[field.translationKey || field.name]}
              values={fieldValue}
              initialValues={field.initialValues}
              fieldTypes={field.fieldTypes}
              error={errors[field.name]}
              isDisabled={
                field.isDisabled ||
                (field.isDisabledCheck
                  ? field.isDisabledCheck(values)
                  : undefined)
              }
              onChange={this.handleFildsChange(field)}
            />
          </div>
        );
      case PEOPLE_INFO:
        return (
          <div key={field.name} className={fieldClasses}>
            <PeopleInfoFields
              translations={formLabels[field.translationKey || field.name]}
              values={fieldValue}
              fieldTypes={field.fieldTypes}
              onChange={this.handleFildsChange(field)}
            />
          </div>
        );
      case VOTE:
        return (
          <div key={field.name} className={fieldClasses}>
            <VoteField
              translations={formLabels}
              field={field}
              vote={fieldValue}
              errorMessage={errorMessage}
              hasError={hasError}
              onChange={this.handleVoteCandidateChange(field)}
            />
          </div>
        );
      case NUMBER_STEPPER:
        return (
          <div key={field.name} className={fieldClasses}>
            <NumberStepper
              translations={formLabels}
              field={field}
              value={fieldValue}
              errorMessage={errorMessage}
              hasError={hasError}
              onChange={this.handleFildsChange(field)}
            />
          </div>
        );
      case RECRUITMENT_STEPS:
        return (
          <div key={field.name} className={fieldClasses}>
            <RecruitmentStepsFields
              translations={formLabels[field.translationKey || field.name]}
              steps={fieldValue}
              initialSteps={field.initialSteps}
              options={field.options}
              fieldTypes={field.fieldTypes}
              error={errors[field.name]}
              onChange={this.handleFildsChange(field)}
            />
          </div>
        );
      case SLIDER:
        return (
          <div key={field.name} className={fieldClasses}>
            <ProgressField
              label={formLabels[field.translationKey || field.name]?.label}
              value={fieldValue}
              isDisabled={
                field.isDisabledCheck ? field.isDisabledCheck(values) : false
              }
              onChange={this.handleSliderChange(field)}
            />
          </div>
        );
      case CYCLE_STEPS:
        return (
          <div key={field.name} className={fieldClasses}>
            <CycleStepsField
              translations={formLabels[field.translationKey || field.name]}
              steps={fieldValue}
              settingsValue={values[field?.settingsKey]}
              settingsKey={field?.settingsKey}
              values={values}
              errors={errors}
              hasError={hasError}
              errorMessage={errorMessage}
              onValidate={this.onValidate(field)}
              onResetSettings={this.onResetDependants(field)}
              onChange={this.onChange(field)}
              onSelect={setFieldValue}
            />
          </div>
        );
      case CYCLE_STEP_ASSIGNMENTS:
        return (
          <Fade
            key={`cycle_step_assignments_${field?.fieldGroup || ''}${
              field.name
            }`}
            in={field.shouldRender && field.shouldRender(values)}
            timeout={{
              enter: DEFAULT_FADE_IN,
              exit: field.shouldFadeOutInstant ? 0 : DEFAULT_FADE_OUT,
            }}
            unmountOnExit
          >
            <div
              className={classNames(classes.field, classes.fieldWrapperClass)}
            >
              <CycleStepAssignmentsField
                translations={formLabels[field.translationKey || field.name]}
                assignments={fieldValue}
                onToggleUserExclusion={this.onToggleUserExclusion(field)}
              />
            </div>
          </Fade>
        );
      case TOGGLEABLE_SETTINGS:
        if (field.isToggleable) {
          return (
            <Fade
              key={`toggleable_settings_field_${field?.settingsGroup || ''}_${
                field?.settingsFor || ''
              }${field.name}`}
              in={field.shouldRender(values)}
              timeout={{
                enter: DEFAULT_FADE_IN,
                exit: field.shouldFadeOutInstant ? 0 : DEFAULT_FADE_OUT,
              }}
              unmountOnExit
            >
              <div
                className={classNames(
                  {
                    [classes.field]:
                      !field.hasDivider && !field.shouldRemoveFieldSpacing,
                    [classes.hasError]: field?.settingsGroup
                      ? !!errors[field.settingsGroup]?.[field.settingsFor]
                      : !!errors[field.settingsFor],
                    [classes.divider]: field.hasDivider,
                  },
                  field.fieldWrapperClass
                )}
              >
                <ToggleableSettingsField
                  translations={formLabels[field.translationKey]}
                  label={formLabels[field.translationKey || field.name]?.label}
                  icon={field.icon}
                  value={fieldValue}
                  settingsFor={field.settingsFor}
                  isActive={values[field.settingsFor]}
                  isSubSetting={field.isSubSetting}
                  onSelect={this.onChange(field)}
                  onToggle={this.onToggleSettings(field)}
                />
              </div>
            </Fade>
          );
        }
        return (
          <div
            key={`toggleable_settings_field_${field?.settingsGroup || ''}_${
              field?.settingsFor || ''
            }${field.name}`}
            className={classNames(
              {
                [classes.field]:
                  !field.hasDivider && !field.shouldRemoveFieldSpacing,
                [classes.hasError]: field?.settingsGroup
                  ? !!errors[field.settingsGroup]?.[field.settingsFor]
                  : !!errors[field.settingsFor],
                [classes.divider]: field.hasDivider,
              },
              field.fieldWrapperClass
            )}
          >
            <ToggleableSettingsField
              translations={formLabels[field.translationKey]}
              label={formLabels[field.translationKey || field.name]?.label}
              icon={field.icon}
              value={fieldValue}
              info={field.prepareInfo ? field.prepareInfo(values) : ''}
              settingsFor={field.settingsFor}
              isActive={values[field.settingsFor]}
              isSubSetting={field.isSubSetting}
              onSelect={this.onChange(field)}
              onToggle={this.onToggleSettings(field)}
            />
          </div>
        );
      case SETTINGS_SUMMARY:
        if (field.isToggleable) {
          return (
            <Fade
              key={`settings_summary_field_${field?.settingsGroup || ''}_${
                field?.settingsKey || ''
              }${field.name}`}
              in={field.shouldRender(values)}
              timeout={{
                enter: DEFAULT_FADE_IN,
                exit: field.shouldFadeOutInstant ? 0 : DEFAULT_FADE_OUT,
              }}
              unmountOnExit
            >
              <div
                className={classNames(
                  {
                    [classes.field]: !field.hasDivider,
                    [classes.hasError]: field?.settingsGroup
                      ? !!errors[field.settingsGroup]?.[field.settingsKey]
                      : !!errors[field.settingsKey],
                    [classes.divider]: field.hasDivider,
                  },
                  field.fieldWrapperClass
                )}
              >
                <SettingsSummaryField
                  translations={formLabels[field.translationKey]}
                  value={fieldValue}
                  info={field.prepareInfo ? field.prepareInfo(values) : ''}
                  settingsKey={field.settingsKey}
                  settingsValueCount={field.prepareValueCount(
                    field?.settingsGroup
                      ? values[field.settingsGroup]?.[field.settingsKey]
                      : values[field.settingsKey]
                  )}
                  hasError={
                    field?.settingsGroup
                      ? !!errors[field.settingsGroup]?.[field.settingsKey]
                      : !!errors[field.settingsKey]
                  }
                  errorMessage={
                    field.settingsGroup
                      ? validationMessages[
                          errors?.[field.settingsGroup]?.[field.settingsKey]
                        ]
                      : validationMessages[errors[field.settingsKey]]
                  }
                  icon={field.icon}
                  isSubSetting={field.isSubSetting}
                  onSelect={this.onChange(field)}
                />
              </div>
            </Fade>
          );
        }
        return (
          <div
            key={`settings_summary_field_${field?.settingsGroup || ''}_${
              field?.settingsKey || ''
            }${field.name}`}
            className={classNames(
              {
                [classes.field]: !field.hasDivider,
                [classes.hasError]: field?.settingsGroup
                  ? !!errors[field.settingsGroup]?.[field.settingsKey]
                  : !!errors[field.settingsKey],
                [classes.divider]: field.hasDivider,
              },
              field.fieldWrapperClass
            )}
          >
            <SettingsSummaryField
              translations={formLabels[field.translationKey]}
              value={fieldValue}
              settingsKey={field.settingsKey}
              settingsValueCount={field.prepareValueCount(
                field?.settingsGroup
                  ? values[field.settingsGroup]?.[field.settingsKey]
                  : values[field.settingsKey]
              )}
              hasError={
                field?.fieldGroup
                  ? !!errors[field.fieldGroup]?.[field.settingsKey]
                  : !!errors[field.settingsKey]
              }
              errorMessage={
                field.settingsGroup
                  ? validationMessages[
                      errors?.[field.settingsGroup]?.[field.settingsKey]
                    ]
                  : validationMessages[errors[field.settingsKey]]
              }
              icon={field.icon}
              isSubSetting={field.isSubSetting}
              onSelect={this.onChange(field)}
            />
          </div>
        );
      case ABSENCES:
        return (
          <div key={field.name} className={fieldClasses}>
            <AbsencesField
              translations={formLabels[field.translationKey || field.name]}
              initialAbsences={initialValues[field.name]}
              absences={fieldValue}
              absenceIcons={this.props.absenceIcons}
              accrualOptions={this.props.accrualOptions}
              errors={errors?.[field.name]}
              onChange={this.onChangeMultipleField(field)}
            />
          </div>
        );
      default:
        return null;
    }
  };

  resolveColumnClass = (columnClass, values) => {
    if (typeof columnClass === 'function') {
      return columnClass(values);
    }

    return columnClass;
  };

  renderContent = (hasColumns = false) => {
    const { classes, customColumnsClass, fields, columns, values } = this.props;

    if (hasColumns) {
      return (
        <div
          ref={this.setContentNode}
          className={classNames(classes.columns, customColumnsClass)}
        >
          {columns.map(column => {
            const columnTitle = column.getTitle
              ? column.getTitle(values, column.title)
              : column.title;

            return (
              <div
                key={`column_${column.id}`}
                className={classNames(
                  classes.columnScrollWrapper,
                  this.resolveColumnClass(
                    column?.columnScrollWrapperClass,
                    values
                  )
                )}
              >
                <Scrollbars
                  verticalScrollClass={classNames(
                    classes.columnScrollY,
                    column.scrollYClass
                  )}
                >
                  <div
                    className={classNames(
                      classes.columnContentWrapper,
                      this.resolveColumnClass(
                        column?.columnContentWrapperClass,
                        values
                      )
                    )}
                  >
                    <div
                      className={classNames(
                        classes.column,
                        column?.columnClass
                      )}
                    >
                      {columnTitle && (
                        <Typography
                          variant={column.titleVariant || 'h5'}
                          component="div"
                          className={classNames(
                            classes.columnTitle,
                            column.columnTitleClass
                          )}
                        >
                          {columnTitle}
                        </Typography>
                      )}
                      {column.fields.map(field => this.renderField(field))}
                    </div>
                  </div>
                </Scrollbars>
              </div>
            );
          })}
        </div>
      );
    }

    return fields.map(field => this.renderField(field));
  };

  handleSubmit = async saveType => {
    const { setFieldValue, submitForm, dirty, isInitialValid } = this.props;

    if (this.questionnaireFlushRef) {
      await this.questionnaireFlushRef();
    }

    if (isInitialValid) {
      setFieldValue(DIRTY, dirty);
    }
    setFieldValue(SAVE_TYPE, saveType);

    submitForm();
  };

  render() {
    const {
      classes,
      titleClass,
      customHeaderClass,
      customScrollWrapperClass,
      customContentClass,
      customFormClass,
      translations,
      isOpened,
      values,
      errors,
      notifyAll,
      hasSaveDraftButton,
      isSubmitting,
      dirty,
      columns,
      isInitialValid,
      isWholeScreen,
      isFullWidth,
      isMiddleWidth,
      isLarge,
      isSmallWidth,
      hasCancelButton,
      hasAddButton,
      hasPreview,
      openPreview,
      isFinish,
      titleText,
      titleIcon: TitleIcon,
      saveButtonText,
      hideDelete,
      shouldHideSave,
      onDelete,
    } = this.props;

    const hasErrors = errorObj => {
      if (!errorObj) return false;

      return Object.values(errorObj).some(val => {
        if (!val) return false;
        if (typeof val === 'object') return hasErrors(val);
        return Boolean(val);
      });
    };

    const saveButtonContent = () => {
      if (isFinish) {
        return translations.finish;
      } else if (saveButtonText) {
        return saveButtonText;
      }
      return translations.save;
    };

    return (
      <Drawer
        anchor="right"
        classes={{
          paper: classNames({
            [classes.wholeScreen]: isWholeScreen,
            [classes.fullWidth]: isFullWidth,
            [classes.middleWidth]: isMiddleWidth,
            [classes.largeWidth]: isLarge,
            [classes.smallWidth]: isSmallWidth,
          }),
        }}
        open={isOpened}
        onClose={this.handleClose}
      >
        <div
          className={classNames(
            classes.header,
            {
              [classes.headerFullWidth]: isFullWidth || isWholeScreen,
            },
            customHeaderClass
          )}
        >
          {TitleIcon && <TitleIcon className={classes.titleIcon} />}
          <Typography
            className={classNames(classes.title, titleClass)}
            variant="h4"
          >
            {this.renderTitle(titleText)}
          </Typography>
          <div className={classes.headerActions}>
            {isInitialValid && !hideDelete ? (
              <ActionButton
                className={classes.deleteIcon}
                type={ACTION_BUTTON_TYPES.DELETE_FILL}
                onClickHandler={() => onDelete(values)}
              />
            ) : null}
            <ActionButton
              type={ACTION_BUTTON_TYPES.CLOSE}
              onClickHandler={this.handleClose}
            />
          </div>
        </div>
        {!isArrayEmpty(columns) ? (
          <>{isOpened ? <form>{this.renderContent(true)}</form> : null}</>
        ) : (
          <div
            className={classNames(
              classes.scrollWrapper,
              customScrollWrapperClass
            )}
          >
            <Scrollbars verticalScrollClass={classes.scrollY}>
              <div
                ref={this.setContentNode}
                className={classNames(
                  classes.content,
                  { [classes.contentFullWidth]: isFullWidth || isWholeScreen },
                  customContentClass
                )}
              >
                {isOpened ? (
                  <form
                    onSubmit={e => e.preventDefault()}
                    className={classNames(classes.form, customFormClass)}
                  >
                    {this.renderContent()}
                  </form>
                ) : null}
              </div>
            </Scrollbars>
          </div>
        )}
        <div
          className={classNames(classes.footer, {
            [classes.footerFullWidth]: isFullWidth,
            [classes.footerWithPreview]: hasPreview || hasSaveDraftButton,
          })}
        >
          {hasSaveDraftButton && (
            <CustomButton
              className={classNames(
                classes.saveButton,
                classes.saveDraftButton
              )}
              onClick={
                !isFinish && (dirty || !isInitialValid)
                  ? () => this.handleSubmit(SAVE)
                  : this.handleClose
              }
              disabled={false}
              type="withTextDarkRounded"
            >
              {translations.saveDraft}
            </CustomButton>
          )}
          {notifyAll && (
            <div className={classes.notifyAll}>
              <CustomCheckbox
                customLabelClass={classes.checkboxLabel}
                isChecked={values[notifyAll.name]}
                labelText={
                  translations.formLabels[
                    notifyAll.translationKey || notifyAll.name
                  ].labelText
                }
                disabled={notifyAll.isDisabled(values)}
                onChange={this.handleCheckboxChange(notifyAll)}
                isControlled
                smallText
              />
            </div>
          )}
          {hasPreview && (
            <Typography
              variant="subtitle1"
              className={classes.preview}
              onClick={openPreview}
            >
              {translations.preview}
            </Typography>
          )}
          <div>
            {hasCancelButton && (
              <CustomButton
                className={classes.cancelButton}
                onClick={this.handleClose}
                type="addWithTextRounded"
              >
                {translations.cancel}
              </CustomButton>
            )}
            {!shouldHideSave && (
              <CustomButton
                className={classNames(classes.saveButton, {
                  [classes.saveButtonSecondary]: hasAddButton,
                  [classes.saveWithDraftButton]: hasSaveDraftButton,
                })}
                onClick={
                  !isFinish && (dirty || !isInitialValid)
                    ? () => this.handleSubmit(SAVE)
                    : this.handleClose
                }
                disabled={isSubmitting || hasErrors(errors)}
                type="withTextDarkRounded"
              >
                {saveButtonContent()}
              </CustomButton>
            )}
            {hasAddButton && (
              <CustomButton
                className={classes.saveAndAddButton}
                onClick={() => this.handleSubmit(SAVE_AND_ADD)}
                type="withTextDarkRounded"
                disabled={isSubmitting || hasErrors(errors)}
              >
                {translations.saveAndAdd}
              </CustomButton>
            )}
          </div>
        </div>
      </Drawer>
    );
  }
}

CustomFormDrawerLayout.defaultProps = {
  titleClass: null,
  customHeaderClass: null,
  customScrollWrapperClass: null,
  customContentClass: null,
  customColumnsClass: null,
  customFormClass: undefined,
  isWholeScreen: false,
  isFullWidth: false,
  isMiddleWidth: false,
  isLarge: false,
  isSmallWidth: false,
  hasCancelButton: false,
  hasAddButton: false,
  isInitialValid: false,
  shouldHideSave: false,
  columns: [],
  fields: [],
  notifyAll: null,
  hasSaveDraftButton: false,
  onDelete: () => {},
  hasPreview: false,
  openPreview: () => {},
  getTitle: () => {},
  isFinish: false,
  titleText: '',
  titleIcon: null,
  saveButtonText: '',
  hideDelete: false,
};

CustomFormDrawerLayout.propTypes = {
  classes: PropTypes.object.isRequired,
  titleClass: PropTypes.string,
  customHeaderClass: PropTypes.string,
  customScrollWrapperClass: PropTypes.string,
  customContentClass: PropTypes.string,
  customFormClass: PropTypes.string,
  customColumnsClass: PropTypes.string,
  translations: PropTypes.object.isRequired,
  isWholeScreen: PropTypes.bool,
  isFullWidth: PropTypes.bool,
  isMiddleWidth: PropTypes.bool,
  isLarge: PropTypes.bool,
  isSmallWidth: PropTypes.bool,
  isOpened: PropTypes.bool.isRequired,
  shouldHideSave: PropTypes.bool,
  fields: PropTypes.arrayOf(PropTypes.shape({})),
  columns: PropTypes.arrayOf(PropTypes.shape({})),
  values: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  dirty: PropTypes.bool.isRequired,
  isInitialValid: PropTypes.bool,
  hasCancelButton: PropTypes.bool,
  hasAddButton: PropTypes.bool,
  hasPreview: PropTypes.bool,
  notifyAll: PropTypes.shape({}),
  hasSaveDraftButton: PropTypes.bool,
  submitForm: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  resetForm: PropTypes.func.isRequired,
  openPreview: PropTypes.func,
  getTitle: PropTypes.func,
  isFinish: PropTypes.bool,
  titleText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  titleIcon: PropTypes.object,
  saveButtonText: PropTypes.string,
  hideDelete: PropTypes.bool,
};

export default withStyles(styles)(CustomFormDrawerLayout);
